// UniversityBrandAdminPage, /university-brand/admin
// White-label org admin: branding, subdomain claim, org-type, courses, staff, learners, analytics.
// Wildcard *.stillwatersacademy.com is live on Vercel, subdomain is instant on save.
// public_slug has a UNIQUE index in DB, uniqueness enforced at two layers (live check + DB constraint).
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  GraduationCap, Users, BookOpen, Plus, Loader2, X, Sparkles,
  Palette, BarChart3, Upload, Globe, Shield, CheckCircle, XCircle, AlertCircle, CreditCard,
  Settings, ExternalLink, Trash2, Edit, Menu, ArrowLeft, Eye, EyeOff, Mail, ChevronDown,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import SEO from '@/components/layout/SEO'
import SyllabusGenerator from '@/components/education/SyllabusGenerator'
import KnowledgeUploader from '@/components/education/KnowledgeUploader'
import CreateCourseModal, { CreateCoursePayload } from '@/components/education/CreateCourseModal'
import CatalogStreamsCard from '@/components/education/CatalogStreamsCard'
import BrandLogo from '@/components/common/BrandLogo'
import { cleanLogoFile, convertUrlToCleanPngBlob } from '@/utils/cleanLogoImage'
import { cloneCommunityCourse } from '@/utils/communityLibrary'
import { cleanCourseText } from '@/utils/textClean'
import { ORG_VOCAB, type OrgType } from '@/contexts/SubdomainBrandingContext'
import { useTranslation } from 'react-i18next';
import LanguagePicker from '@/components/university/ui/LanguagePicker'

const M = '#06060c'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const GR = '#4ade80'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const RED = '#f87171'
const serif = "'Playfair Display', Georgia, serif"

interface OrgConfig {
  id: OrgType
  label: string
  emoji: string
  tagline: string
  staffLabel: string
  learnerLabel: string
  courseLabel: string
  staffSingular: string
  learnerSingular: string
  courseSingular: string
  headerSub: string
}

// ── Single source of truth for wording: ORG_VOCAB (SubdomainBrandingContext)
//, the same table /education-admin, /learn, and the invite emails read. This
// console keeps only its own extras (emoji, tagline, header, public label) and
// derives every staff/learner/course label, plural AND singular, from the
// shared vocabulary. Fixes the Trainers-vs-Course-Managers drift and the
// 'Generate studie' singular bug (church 'Studies' through the old helper).
const ORG_META: Record<OrgType, { label: string; emoji: string; tagline: string; headerSub: string }> = {
  school: { label: 'School', emoji: '🏫', tagline: 'K-12 education with AI-powered lessons and verifiable results', headerSub: 'School administration, theory delivered, evidence verified' },
  university: { label: 'University / College', emoji: '🎓', tagline: 'Higher education with certified credentials employers can verify in 30 seconds', headerSub: 'University administration, theory delivered, evidence verified' },
  ngo: { label: 'NGO / Non-profit', emoji: '🌍', tagline: 'Training programs and community learning with impact evidence', headerSub: 'NGO learning hub, evidence of community impact' },
  church: { label: 'Church / Faith community', emoji: '⛪', tagline: 'Biblical studies, discipleship tracks, and faith formation with verified completion', headerSub: 'Faith learning hub, discipleship with evidence' },
  business: { label: 'Business / Corporate', emoji: '🏢', tagline: 'Staff training and professional development with credentials for compliance and HR', headerSub: 'Corporate learning platform, staff development, verified' },
  sports_club: { label: 'Sports club / Academy', emoji: '⚽', tagline: 'Athlete education, coaching certifications, and sports science programs', headerSub: 'Sports academy, athlete education and certification' },
  community: { label: 'Community learning hub', emoji: '🤝', tagline: 'Open community education, lifelong learning for all ages', headerSub: 'Community hub, lifelong learning, evidence of growth' },
  family_edu: { label: 'Learning Centre / Co-op', emoji: '🏡', tagline: 'Personalised learning, verified results', headerSub: 'Learning centre administration, theory delivered, evidence verified' },
  platform: { label: 'Platform / Academy', emoji: '🏛️', tagline: 'Unified educational ecosystem with accredited credentials', headerSub: 'Platform administration, theory delivered, evidence verified' },
}

const ORG_NAME_PLACEHOLDERS: Record<OrgType, string> = {
  school: 'e.g. Westside Learning Academy',
  university: 'e.g. Springfield Institute of Technology',
  ngo: 'e.g. Bright Futures Foundation',
  church: 'e.g. Grace Community Church',
  business: 'e.g. Acme Professional Training',
  sports_club: 'e.g. Riverside Sports Academy',
  community: 'e.g. Lakeside Learning Collective',
  family_edu: 'e.g. Riverside Learning Co-op',
  platform: 'e.g. Global Learning Academy',
}

const ORG_TYPES: OrgConfig[] = (['school', 'university', 'ngo', 'church', 'business', 'sports_club', 'community'] as OrgType[]).map(id => ({
  id,
  ...ORG_META[id],
  staffLabel: ORG_VOCAB[id].staffPlural,
  learnerLabel: ORG_VOCAB[id].learnerPlural,
  courseLabel: ORG_VOCAB[id].coursePlural,
  staffSingular: ORG_VOCAB[id].staffSingular,
  learnerSingular: ORG_VOCAB[id].learnerSingular,
  courseSingular: ORG_VOCAB[id].courseSingular,
}))

const getOrg = (type: OrgType | null | undefined): OrgConfig =>
  ORG_TYPES.find(o => o.id === type) ?? ORG_TYPES[1]

const RESERVED = new Set([
  'www','app','api','admin','mail','smtp','ftp','cdn','static','assets',
  'status','support','help','verify','credentials','dashboard','learn','signin','signup',
])

const toSlug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9 -]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 30)

// ── Types ─────────────────────────────────────────────────────────────────────
interface Branding {
  display_name: string; logo_url: string | null
  primary_color: string; secondary_color: string
  subdomain: string | null; org_type: OrgType | null
}
interface StaffMember { id: string; name: string | null; email: string; is_active: boolean; facility_role?: string | null }
interface OrgCourse {
  id: string; name: string; delivery_mode: 'theory' | 'practical' | 'hybrid'
  module_count: number | null; lesson_count: number | null; enrolled_count: number
  is_published: boolean
  enrollment_mode?: 'all' | 'targeted'
  enrolled_student_ids?: string[]
  teacher_id?: string | null
}
interface MemberRow { id: string; name: string | null; email: string; is_active: boolean }
type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved'
type Tab = 'branding' | 'courses' | 'staff' | 'learners' | 'analytics' | 'billing'

// ═══════════════════════════════════════════════════════════════════════════════
export default function UniversityBrandAdminPage() {
  const { t } = useTranslation();

  const { user, userProfile, isLoading: authLoading, signIn } = useAuth()
  const { isAdmin } = useAdmin()
  const navigate = useNavigate()

  const [orgType, setOrgType] = useState<OrgType | null>(
    () => (new URLSearchParams(window.location.search).get('type') as OrgType | null)
  )
  const [branding, setBranding] = useState<Branding>({
    display_name: '', logo_url: null, primary_color: '#1E3A8A',
    secondary_color: '#269733', subdomain: null, org_type: null,
  })
  const org = getOrg(orgType || branding.org_type)
  const [logoCleaning, setLogoCleaning] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // ── Anonymous prospect → inline org creation (kills the login wall).
  // register-whitelabel-org creates facility + auth user with admin metadata
  // (handle_new_user populates the profile); signIn then hands the session to
  // AuthContext, which resolves user → profile → facility → this same page
  // re-renders as the wizard.
  const [wlOrgName, setWlOrgName] = useState('')
  const [wlEmail, setWlEmail] = useState('')
  const [wlPassword, setWlPassword] = useState('')
  const [wlLoading, setWlLoading] = useState(false)
  const [wlError, setWlError] = useState<string | null>(null)

  const handleOrgSignup = async () => {
    if (!wlOrgName.trim() || !wlEmail.trim() || wlPassword.length < 8) return
    setWlLoading(true); setWlError(null)
    try {
      const { data, error } = await supabase.functions.invoke('register-whitelabel-org', {
        body: {
          email: wlEmail.trim(),
          password: wlPassword,
          org_name: wlOrgName.trim(),
          org_type: orgType || null,
        },
      })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      const { error: signInErr } = await signIn(wlEmail.trim(), wlPassword)
      if (signInErr) throw new Error('Account created. Please sign in to continue.')
    } catch (e: any) {
      setWlError(e.message || 'Registration failed.')
    } finally {
      setWlLoading(false)
    }
  }

  const [activeTab, setActiveTab] = useState<Tab>('branding')
  const [loading, setLoading] = useState(true)
  const hasLoadedRef = useRef(false)
  const [facilityId, setFacilityId] = useState<string | null>(null)
  const [currentSlug, setCurrentSlug] = useState<string | null>(null)
  const [subdomainInput, setSubdomainInput] = useState('')
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle')
  const [slugEditing, setSlugEditing] = useState(false)
  const slugTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [courses, setCourses] = useState<OrgCourse[]>([])
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState<'teacher' | 'admin'>('teacher')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteMsg, setInviteMsg] = useState<string | null>(null)
  const [showGen, setShowGen] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [courseName, setCourseName] = useState('')
  const [courseStep, setCourseStep] = useState(false)
  const [newCourseCategory, setNewCourseCategory] = useState('')
  const [newCourseAge, setNewCourseAge] = useState('18')
  const [newCourseCategories, setNewCourseCategories] = useState<{ id: string; name: string }[]>([])
  const [creatingCourse, setCreatingCourse] = useState(false)
  const [newCourseId, setNewCourseId] = useState<string | null>(null)
  const [newCourseUseSearch, setNewCourseUseSearch] = useState(false)
  const [courseError, setCourseError] = useState<string | null>(null)

  // Reset course-creation state whenever the modal closes
  useEffect(() => {
    if (!showGen) {
      setCourseStep(false); setCourseName(''); setNewCourseId(null)
      setNewCourseCategory(''); setCourseError(null); setNewCourseUseSearch(false)
    }
  }, [showGen])
  const [learnerEmail, setLearnerEmail] = useState('')
  const [learnerClass, setLearnerClass] = useState('')
  const [learnerLoading, setLearnerLoading] = useState(false)
  const [learnerMsg, setLearnerMsg] = useState<string | null>(null)
  // Transfer flow state
  const [transferPending, setTransferPending] = useState<{
    email: string; user_id: string; user_name: string
    target_role?: string
    sent?: boolean
  } | null>(null)
  const [transferLoading, setTransferLoading] = useState(false)
  const [inviteHint, setInviteHint] = useState<string | null>(null)
  const [members, setMembers] = useState<MemberRow[]>([])
  const [memberBusy, setMemberBusy] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [memberMsg, setMemberMsg] = useState<string | null>(null)

  // ── Billing & Seats (commerce v1: checkout via billing, generic webhook grant;
  //    per-seat learner provisioning + update_seats arrive with the v2 webhook branch) ──
  const [seatCount, setSeatCount] = useState(10)
  const [billingInterval, setBillingInterval] = useState<'month' | 'six_months' | 'year'>('month')
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)
  const [wlPlan, setWlPlan] = useState<{ price_monthly: number; price_six_monthly: number; price_yearly: number } | null>(null)
  const [seatInfo, setSeatInfo] = useState<{ total: number; used: number } | null>(null)

  const wlPlanKey = (orgType === 'school' || orgType === 'ngo') ? 'wl_school' : 'wl_business'

  // Live pricing from subscription_plans, single source of truth, no hardcoded prices
  useEffect(() => {
    if (!facilityId) return
    supabase.from('subscription_plans')
      .select('price_monthly, price_six_monthly, price_yearly')
      .eq('plan_key', wlPlanKey)
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data }: any) => { if (data) setWlPlan(data) })
  }, [facilityId, wlPlanKey])

  const buySeats = async () => {
    if (!facilityId) return
    setCheckoutLoading(true); setCheckoutError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/billing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          action: 'checkout', plan: wlPlanKey, interval: billingInterval,
          quantity: seatCount, destination: 'whitelabel', facility_id: facilityId,
        }),
      })
      const data = await res.json()
      if (data?.url) { window.location.href = data.url; return }
      throw new Error(data?.error || 'Checkout failed')
    } catch (e: any) {
      setCheckoutError(e.message || 'Checkout failed')
    } finally {
      setCheckoutLoading(false)
    }
  }

    const openPortal = async () => {
    if (!facilityId || portalLoading) return
    setPortalLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/billing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'portal', destination: 'whitelabel' }),
      })
      const data = await res.json()
      if (data?.url) { window.location.href = data.url; return }
      throw new Error(data?.error || 'Could not open the billing portal')
    } catch (e: any) {
      setCheckoutError(e.message || 'Could not open the billing portal')
    } finally {
      setPortalLoading(false)
    }
  }

  const intervalPrice = billingInterval === 'month' ? wlPlan?.price_monthly
    : billingInterval === 'six_months' ? wlPlan?.price_six_monthly
      : wlPlan?.price_yearly
  const checkoutTotal = wlPlan && intervalPrice ? seatCount * intervalPrice : null

  // ── Auth guard ──
  useEffect(() => {
    if (authLoading || !user) return
    if (!isAdmin && !userProfile?.facility_id) { navigate('/signin'); return }
    if (!isAdmin && userProfile?.facility_role !== 'admin') {
      const dest = userProfile?.facility_role === 'teacher' ? '/education-admin' : '/learn'
      navigate(dest); return
    }
    const queryFacility = new URLSearchParams(window.location.search).get('facility_id')
    setFacilityId(queryFacility || userProfile?.facility_id || '00000000-0000-0000-0000-000000000001')
  }, [user, userProfile, authLoading, isAdmin, navigate])

  // ── Load data ──
  const load = useCallback(async (isInitial = false) => {
    if (!facilityId) return
    if (isInitial || !hasLoadedRef.current) {
      setLoading(true)
    }

    try {
      const { data: fac } = await supabase
        .from('facilities')
        .select('name, branding, public_slug, facility_type, total_seats, active_seats')
        .eq('id', facilityId)
        .single()

      if (fac) {
        setSeatInfo({ total: fac.total_seats ?? 0, used: fac.active_seats ?? 0 })
        const b = (fac.branding as any) || {}
        const rawSlug = fac.public_slug || b.subdomain || null
        const type = (b.org_type || null) as OrgType | null
        // The DB auto-assigns "{name}-{first 4 of id}" on insert, a collision-
        // proof placeholder, NOT a claim. Treat it as unclaimed: prefill the clean
        // org name and let the live availability check validate before claiming.
        // Suffix check (not exact match): JS toSlug and the DB regex can differ on
        // accents/punctuation/truncation, only the id-suffix proves auto-assignment.
        const isAutoSlug = !!rawSlug && rawSlug.endsWith('-' + facilityId.slice(0, 4))
        const claimed = !!rawSlug && !isAutoSlug
        setBranding({
          display_name: b.display_name || fac.name || '',
          logo_url: b.logo_url || null,
          primary_color: b.primary_color || '#1E3A8A',
          secondary_color: b.secondary_color || '#269733',
          subdomain: claimed ? rawSlug : null, org_type: type,
        })
        if (claimed) {
          setSubdomainInput(rawSlug!)
          setCurrentSlug(rawSlug)
          setSlugEditing(false)
        } else {
          setSubdomainInput(toSlug(b.display_name || fac.name || ''))
          setCurrentSlug(null)
          setSlugEditing(true)
        }
        setOrgType(type)
      }

      // facility_id (not owned_by_facility): catalog copies and teacher-created
      // classes set facility_id only, owned_by_facility hid them from this list
      const { data: cls } = await supabase
        .from('classes')
        .select('id,name,delivery_mode,module_count,lesson_count,is_published,enrollment_mode,teacher_id,class_enrollments(student_id)')
        .eq('facility_id', facilityId)
        .eq('is_active', true)
        .order('name')
      setCourses((cls || []).map((c: any) => {
        const studentIds = (c.class_enrollments || []).map((e: any) => e.student_id)
        return {
          id: c.id, name: c.name,
          delivery_mode: c.delivery_mode || 'theory',
          module_count: c.module_count, lesson_count: c.lesson_count,
          enrolled_count: studentIds.length,
          enrolled_student_ids: studentIds,
          is_published: !!c.is_published,
          enrollment_mode: (c.enrollment_mode as any) || 'all',
          teacher_id: c.teacher_id ?? null,
        }
      }))

      const { data: s } = await supabase
        .from('users')
        .select('id,name,email,is_active,facility_role')
        .eq('facility_id', facilityId)
        .in('facility_role', ['teacher', 'admin'])
      setStaff((s || []) as StaffMember[])

      // Learner roster, deactivation releases the seat (manage-member)
      const { data: mem } = await supabase
        .from('users')
        .select('id,name,email,is_active')
        .eq('facility_id', facilityId)
        .eq('facility_role', 'member')
        .order('name')
      setMembers((mem || []) as MemberRow[])

      // Subject-area options, ONLY categories the audited master catalog teaches
      // (same pattern as CreateClassForm). Unfiltered, this surfaces community/
      // elective seeds AND elite-track categories; an elite category would trip
      // the SessionGate elite check and bounce the org's own members.
      const MASTER_HUB = '00000000-0000-0000-0000-000000000001'
      const ELITE_ROOT = 'e0000000-0000-0000-0000-000000000000'
      const [clsCatRes, catRes] = await Promise.all([
        supabase.from('classes').select('category_id').eq('facility_id', MASTER_HUB).eq('is_active', true),
        supabase.from('categories').select('id, name, parent_id').eq('is_educational', true).order('name').limit(600),
      ])
      const usedCats = new Set((clsCatRes.data || []).map((c: any) => c.category_id))
      const cats = (catRes.data || []).filter((c: any) =>
        usedCats.has(c.id) && c.id !== ELITE_ROOT && c.parent_id !== ELITE_ROOT
      )
      setNewCourseCategories(cats as { id: string; name: string }[])
    } finally {
      setLoading(false)
      hasLoadedRef.current = true
    }
  }, [facilityId])

  useEffect(() => { if (facilityId) load() }, [facilityId, load])

  // ── Course detail / edit modal state & handlers ──
  const [editingCourse, setEditingCourse] = useState<OrgCourse | null>(null)
  const [editTab, setEditTab] = useState<'syllabus' | 'knowledge' | 'settings' | 'access'>('syllabus')
  const [editingPlan, setEditingPlan] = useState<any[] | null>(null)
  const [editLoadingPlan, setEditLoadingPlan] = useState(false)
  const [editCourseName, setEditCourseName] = useState('')
  const [editDeliveryMode, setEditDeliveryMode] = useState<'theory' | 'practical' | 'hybrid'>('theory')
  const [savingSettings, setSavingSettings] = useState(false)
  const [editEnrollmentMode, setEditEnrollmentMode] = useState<'all' | 'targeted'>('all')
  const [enrolledStudentIds, setEnrolledStudentIds] = useState<string[]>([])
  const [savedEnrolledIds, setSavedEnrolledIds] = useState<string[]>([])
  const [loadingEnrolled, setLoadingEnrolled] = useState(false)
  const [savingEnrolled, setSavingEnrolled] = useState(false)
  const [enrolledSearch, setEnrolledSearch] = useState('')
  const [courseMsg, setCourseMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null)
  const [publishingId, setPublishingId] = useState<string | null>(null)
  const [expandedRosterCourseId, setExpandedRosterCourseId] = useState<string | null>(null)
  // Course Managers drill-down: which manager's panel is expanded
  const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null)

  // ── Teacher offboarding (Kit 15) ──
  const [offboardOpen, setOffboardOpen] = useState(false)
  const [offboardDepartingId, setOffboardDepartingId] = useState('')
  const [offboardTargetId, setOffboardTargetId] = useState('')
  const [offboardBusy, setOffboardBusy] = useState(false)
  const [offboardPreview, setOffboardPreview] = useState<{ courses: number; students: number; coTeachLinks: number } | null>(null)
  const [offboardResult, setOffboardResult] = useState<{ done: number; failed: number; errors: string[] } | null>(null)

  const loadOffboardPreview = async (departingId: string) => {
    setOffboardPreview(null)
    if (!departingId) return
    const owned = courses.filter(c => c.teacher_id === departingId)
    let coTeachLinks = 0
    try {
      const { count } = await supabase
        .from('course_co_teachers')
        .select('id', { count: 'exact', head: true })
        .eq('teacher_id', departingId)
      coTeachLinks = count ?? 0
    } catch { /* advisory count only */ }
    let students = 0
    try {
      const { data: assignedRows } = await supabase
        .from('class_enrollments')
        .select('student_id')
        .eq('assigned_teacher_id', departingId)
      students = new Set((assignedRows ?? []).map((r: any) => r.student_id as string)).size
    } catch { /* advisory count only */ }
    setOffboardPreview({ courses: owned.length, students, coTeachLinks })
  }

  const openOffboarding = (member?: MemberRow) => {
    setOffboardOpen(true)
    setOffboardTargetId('')
    setOffboardResult(null)
    const departingId = member?.id ?? ''
    setOffboardDepartingId(departingId)
    loadOffboardPreview(departingId)
  }

  const closeOffboarding = () => {
    if (offboardBusy) return
    setOffboardOpen(false)
    setOffboardResult(null)
  }

  const runOffboarding = async () => {
    const adminId = user?.id
    if (!offboardDepartingId || !offboardTargetId || !adminId || offboardBusy) return
    setOffboardBusy(true)
    const errors: string[] = []
    let done = 0
    // 0) best-effort: retire the departing teacher's pending transfer requests
    try {
      await supabase.from('course_transfer_requests').update({ status: 'declined' })
        .eq('status', 'pending')
        .or(`from_teacher_id.eq.${offboardDepartingId},to_teacher_id.eq.${offboardDepartingId}`)
      await supabase.from('student_teacher_transfer_requests').update({ status: 'declined' })
        .eq('status', 'pending')
        .or(`from_teacher_id.eq.${offboardDepartingId},to_teacher_id.eq.${offboardDepartingId}`)
    } catch (e: any) { errors.push(`Cancelling pending requests: ${e?.message ?? 'failed'}`) }
    // 1) transfer every course they own (admin-initiated requests auto-accept via trigger)
    const owned = courses.filter(c => c.teacher_id === offboardDepartingId)
    for (const c of owned) {
      const { error } = await supabase.from('course_transfer_requests').insert({
        class_id: c.id,
        from_teacher_id: offboardDepartingId,
        to_teacher_id: offboardTargetId,
        requested_by: adminId,
      })
      if (error) errors.push(`Course "${c.name}": ${error.message}`)
      else done += 1
    }
    // 2) reassign every learner currently assigned to them (one request per learner)
    let studentIds: string[] = []
    try {
      const { data: assignedRows } = await supabase
        .from('class_enrollments')
        .select('student_id')
        .eq('assigned_teacher_id', offboardDepartingId)
      studentIds = Array.from(new Set((assignedRows ?? []).map((r: any) => r.student_id as string)))
    } catch (e: any) { errors.push(`Loading assigned learners: ${e?.message ?? 'failed'}`) }
    for (const sid of studentIds) {
      const { error } = await supabase.from('student_teacher_transfer_requests').insert({
        student_id: sid,
        from_teacher_id: offboardDepartingId,
        to_teacher_id: offboardTargetId,
        requested_by: adminId,
      })
      if (error) errors.push(`Learner ${sid.slice(0, 8)}...: ${error.message}`)
      else done += 1
    }
    // 3) remove their co-teaching links (already authorized by the existing
    //    "Primary teacher or admin manages co-teachers" ALL policy — Part 0 was skipped)
    const { error: ctErr } = await supabase.from('course_co_teachers').delete().eq('teacher_id', offboardDepartingId)
    if (ctErr) errors.push(`Co-teacher cleanup: ${ctErr.message}`)
    setOffboardResult({ done, failed: errors.length, errors })
    setOffboardBusy(false)
  }
  // Drill-down learner detail: progress across one manager's courses
  const [staffStudentDetail, setStaffStudentDetail] = useState<{
    member: MemberRow
    rows: { courseId: string; courseName: string; mastery: number | null; lastActivity: string | null }[]
  } | null>(null)
  const [staffStudentLoading, setStaffStudentLoading] = useState(false)

  const openStaffStudentDetail = async (member: MemberRow, managerCourses: OrgCourse[]) => {
    if (managerCourses.length === 0) return
    setStaffStudentLoading(true)
    setStaffStudentDetail({ member, rows: [] })
    try {
      const { data } = await supabase
        .from('student_subject_progress')
        .select('class_id, mastery_score, last_activity_at')
        .eq('user_id', member.id)
        .in('class_id', managerCourses.map(c => c.id))
      const byClass = new Map((data || []).map((r: any) => [r.class_id, r]))
      const relevant = managerCourses.filter(c =>
        (c.enrolled_student_ids || []).includes(member.id) || byClass.has(c.id))
      setStaffStudentDetail({
        member,
        rows: relevant.map(c => ({
          courseId: c.id,
          courseName: c.name,
          mastery: byClass.get(c.id)?.mastery_score ?? null,
          lastActivity: byClass.get(c.id)?.last_activity_at ?? null,
        })),
      })
    } catch {
      setStaffStudentDetail(null)
    } finally {
      setStaffStudentLoading(false)
    }
  }

  useEffect(() => {
    if (!editingCourse) return
    setEditCourseName(editingCourse.name)
    setEditDeliveryMode(editingCourse.delivery_mode)
    setEditEnrollmentMode(editingCourse.enrollment_mode || 'all')
    setEnrolledSearch('')
    setEditLoadingPlan(true)
    setLoadingEnrolled(true)
    supabase.from('classes')
      .select('module_plan')
      .eq('id', editingCourse.id)
      .maybeSingle()
      .then(({ data }) => {
        setEditingPlan(Array.isArray(data?.module_plan) ? data.module_plan : null)
        setEditLoadingPlan(false)
      })

    supabase.from('class_enrollments')
      .select('student_id')
      .eq('class_id', editingCourse.id)
      .then(({ data }) => {
        const activeIds = new Set(members.filter(m => m.is_active).map(m => m.id))
        const ids = (data || []).map((r: any) => r.student_id).filter((id: string) => activeIds.has(id))
        setEnrolledStudentIds(ids)
        setSavedEnrolledIds(ids)
        setLoadingEnrolled(false)
      })
  }, [editingCourse?.id, members])

  const saveEnrollmentAccess = async (mode: 'all' | 'targeted', selectedIds: string[]) => {
    if (!editingCourse) return
    setSavingEnrolled(true)
    const targetName = editingCourse.name
    try {
      const { error: clsErr } = await (supabase.from('classes') as any)
        .update({ enrollment_mode: mode })
        .eq('id', editingCourse.id)
      if (clsErr) throw clsErr

      if (mode === 'targeted') {
        const currentSet = new Set(savedEnrolledIds)
        const selectedSet = new Set(selectedIds)
        const toRemove = [...currentSet].filter(id => !selectedSet.has(id))
        const toAdd = [...selectedSet].filter(id => !currentSet.has(id))

        if (toRemove.length > 0) {
          const { error: rmErr } = await supabase.from('class_enrollments')
            .delete()
            .eq('class_id', editingCourse.id)
            .in('student_id', toRemove)
          if (rmErr) throw rmErr
        }

        if (toAdd.length > 0) {
          const { data: cRow } = await (supabase.from('classes') as any)
            .select('category_id')
            .eq('id', editingCourse.id)
            .maybeSingle()

          const enrollRows = toAdd.map(sid => ({ class_id: editingCourse.id, student_id: sid }))
          const { error: insErr } = await supabase.from('class_enrollments')
            .upsert(enrollRows, { onConflict: 'class_id,student_id', ignoreDuplicates: true })
          if (insErr) throw insErr

          if (cRow?.category_id && facilityId) {
            const progressRows = toAdd.map(uid => ({
              user_id: uid,
              category_id: cRow.category_id,
              class_id: editingCourse.id,
              hub_id: facilityId,
            }))
            const { error: progressErr } = await supabase.from('student_subject_progress')
              .upsert(progressRows, { onConflict: 'user_id,category_id,class_id', ignoreDuplicates: true })
            if (progressErr) throw progressErr
          }
        }
      }

      setEditEnrollmentMode(mode)
      setEnrolledStudentIds(selectedIds)
      setSavedEnrolledIds(selectedIds)
      const count = mode === 'targeted' ? selectedIds.length : members.filter(m => m.is_active).length
      setCourses(prev => prev.map(c => c.id === editingCourse.id ? {
        ...c,
        enrollment_mode: mode,
        enrolled_count: count,
        enrolled_student_ids: mode === 'targeted' ? selectedIds : (c.enrolled_student_ids || []),
      } : c))
      setEditingCourse(null)
      const isDraft = !editingCourse.is_published
      const toastText = (mode === 'targeted' && isDraft)
        ? `✓ Roster saved for "${targetName}". Assigned learners will see this once you Publish.`
        : mode === 'targeted'
          ? `✓ Access saved: "${targetName}" is now targeted to ${selectedIds.length} learner(s).`
          : `✓ Access saved: "${targetName}" is now open to the entire organisation.`
      setCourseMsg({
        text: toastText,
        type: 'success',
      })
      setTimeout(() => setCourseMsg(null), 5000)
      await load()
    } catch (err: any) {
      alert(err.message || 'Failed to save access settings')
    } finally {
      setSavingEnrolled(false)
    }
  }

  const saveCourseSettings = async () => {
    if (!editingCourse || !editCourseName.trim()) return
    setSavingSettings(true)
    try {
      const { error } = await supabase.from('classes')
        .update({
          name: cleanCourseText(editCourseName.trim()),
          delivery_mode: editDeliveryMode,
        })
        .eq('id', editingCourse.id)
      if (error) throw error
      setEditingCourse(prev => prev ? { ...prev, name: cleanCourseText(editCourseName.trim()), delivery_mode: editDeliveryMode } : null)
      setCourseMsg({ text: 'Course settings updated!', type: 'success' })
      setTimeout(() => setCourseMsg(null), 4000)
      await load()
    } catch (err: any) {
      alert(err.message || 'Failed to save settings')
    } finally {
      setSavingSettings(false)
    }
  }

  const deleteCourse = async () => {
    if (!editingCourse) return
    if (!window.confirm(`Delete "${editingCourse.name}"? This action cannot be undone.`)) return
    try {
      const { error } = await supabase.from('classes')
        .update({ is_active: false })
        .eq('id', editingCourse.id)
      if (error) throw error
      setEditingCourse(null)
      setCourseMsg({ text: 'Course deleted.', type: 'success' })
      setTimeout(() => setCourseMsg(null), 4000)
      await load()
    } catch (err: any) {
      alert(err.message || 'Failed to delete course')
    }
  }

  const togglePublish = async (courseId: string, publish: boolean) => {
    setPublishingId(courseId)
    const targetCourse = courses.find(c => c.id === courseId)
    const courseTitle = targetCourse?.name || 'Course'
    try {
      const { data, error } = await supabase.functions.invoke('publish-course', {
        body: { class_id: courseId, action: publish ? 'publish' : 'unpublish' }
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, is_published: publish } : c))
      if (editingCourse?.id === courseId) {
        setEditingCourse(prev => prev ? { ...prev, is_published: publish } : null)
      }
      const isTargetedWithZero = publish && targetCourse?.enrollment_mode === 'targeted' && (targetCourse?.enrolled_count ?? 0) === 0
      const toastText = isTargetedWithZero
        ? `⚠️ "${courseTitle}" is Live, but nobody is assigned, no one will see this course.`
        : publish
          ? `✓ "${courseTitle}" is now Live and published for learners!`
          : `✓ "${courseTitle}" is now Draft (unpublished from learners).`
      setCourseMsg({
        text: toastText,
        type: isTargetedWithZero ? 'error' : 'success',
      })
      setTimeout(() => setCourseMsg(null), 5000)
      await load()
    } catch (err: any) {
      alert(err.message || 'Failed to update publication status')
    } finally {
      setPublishingId(null)
    }
  }

  // ── Slug check ──
  const checkSlug = useCallback(async (raw: string) => {
    const slug = toSlug(raw)
    if (!slug || slug.length < 3) { setSlugStatus('invalid'); return }
    if (RESERVED.has(slug)) { setSlugStatus('reserved'); return }
    if (slug === currentSlug) { setSlugStatus('available'); return }
    setSlugStatus('checking')
    // The facilities table is own-facility-only under RLS, this check would
    // report every taken slug as available. Query the branding view instead:
    // the same source the middleware resolves, so we test exactly what's live.
    const { data } = await (supabase.from('public_facility_branding' as any) as any)
      .select('id').eq('public_slug', slug).maybeSingle()
    setSlugStatus(data ? 'taken' : 'available')
  }, [currentSlug])

  const handleSlugChange = (raw: string) => {
    const slug = toSlug(raw)
    setSubdomainInput(slug)
    setBranding(p => ({ ...p, subdomain: slug || null }))
    setSlugStatus('idle')
    if (slugTimer.current) clearTimeout(slugTimer.current)
    if (slug.length >= 3) {
      setSlugStatus('checking')
      slugTimer.current = setTimeout(() => checkSlug(slug), 600)
    }
  }

  // ── Save ──
  const canSave = !['taken', 'invalid', 'reserved', 'checking'].includes(slugStatus)

  const save = async () => {
    if (!facilityId || !canSave) return
    setSaving(true); setSaveMsg(null)
    const slug = subdomainInput ? toSlug(subdomainInput) : null
    // ── HARD GATE (frontend half) + DB-trigger compatibility ──
    // The database now enforces: branding.org_type set ⟹ facility_type 'platform'
    // (trg_enforce_platform_facility_type, tested live). So this save writes the
    // org_type marker ONLY for platform orgs. Legacy facilities (a school
    // principal here) keep their type AND get no marker, compatible with the
    // trigger, and this surface can never reintroduce the conflation either.
    const { data: facRow } = await supabase
      .from('facilities')
      .select('facility_type, branding')
      .eq('id', facilityId)
      .maybeSingle()
    const existingType = facRow?.facility_type ?? null
    const isPlatformOrg = existingType === 'platform' || existingType == null
    const safeType = isPlatformOrg ? 'platform' : existingType

    // Merge into the LIVE branding row so keys this form doesn't edit
    // (catalog_streams from the Catalog Streams card, future keys) survive.
    const liveBranding: any = (facRow as any)?.branding || {}
    const payload: any = { ...liveBranding, ...branding, subdomain: slug }
    if (isPlatformOrg) {
      payload.org_type = orgType || null
    } else {
      delete payload.org_type
    }

    const { error } = await supabase
      .from('facilities')
      .update({
        branding: payload,
        public_slug: slug,
        facility_type: safeType,
      } as any)
      .eq('id', facilityId)
    if (!error) {
      setCurrentSlug(slug)
      setBranding(p => ({ ...p, subdomain: slug, org_type: orgType }))
      if (slug) setSlugEditing(false)
      setSaveMsg(slug ? `Saved. Live at ${slug}.stillwatersacademy.com` : 'Branding saved.')
    } else {
      setSaveMsg(error.message?.includes('unique') ? 'That subdomain is already taken.' : 'Save failed, please try again.')
    }
    setSaving(false)
  }

  // ── Invite staff ──
  const inviteStaff = async () => {
    if (!inviteEmail.trim() || !facilityId) return
    setInviteLoading(true); setInviteMsg(null)
    try {
      const { data, error } = await supabase.functions.invoke('invite-teacher', {
        body: { email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole },
      })
      if (error) {
        let parsed: any = null
        try { parsed = await (error as any).context?.json?.() } catch { /* ignore */ }
        if (parsed?.requires_transfer) {
          setTransferPending({
            email: inviteEmail.trim(),
            user_id: parsed.user_id,
            user_name: parsed.user_name || inviteName.trim() || inviteEmail.split('@')[0],
            target_role: parsed.target_role || inviteRole,
          })
          return
        }
        throw new Error(parsed?.error || error.message)
      }
      if (data?.error) throw new Error(data.error)
      setInviteMsg(`Invite sent to ${inviteEmail}.`)
      setInviteEmail(''); setInviteName('')
      await load()
    } catch (err: any) { setInviteMsg(err.message || 'Failed to send invite.') }
    finally { setInviteLoading(false) }
  }

  // ── Invite learner ──
  const inviteLearner = async () => {
    if (!learnerEmail.trim() || !facilityId) return
    setLearnerLoading(true); setLearnerMsg(null)
    try {
      const { data, error } = await supabase.functions.invoke('invite-student', {
        body: { email: learnerEmail.trim(), name: learnerEmail.split('@')[0], class_id: learnerClass || null },
      })
      // Parse 409 transfer response
      if (error) {
        let parsed: any = null
        try { parsed = await (error as any).context?.json?.() } catch { /* ignore */ }
        if (parsed?.requires_transfer) {
          setTransferPending({
            email: learnerEmail.trim(),
            user_id: parsed.user_id,
            user_name: parsed.user_name || learnerEmail.split('@')[0],
            target_role: 'member',
          })
          return
        }
        throw new Error(parsed?.error || error.message)
      }
      if (data?.error) throw new Error(data.error)
      setLearnerMsg(`Invite sent to ${learnerEmail}.`)
      setLearnerEmail(''); setLearnerClass('')
      await load()
    } catch (err: any) { setLearnerMsg(err.message || 'Failed to invite.') }
    finally { setLearnerLoading(false) }
  }

  const reinviteWithSecondEmail = () => {
    const name = transferPending?.user_name || ''
    setTransferPending(null)
    setInviteEmail('')
    setInviteName('')
    setLearnerEmail('')
    setInviteHint(name ? `Enter an alternate email for ${name} to invite them directly without a facility transfer.` : 'Enter an alternate email address to invite this user without a facility transfer.')
  }

  const handleSendTransfer = async () => {
    if (!transferPending) return
    setTransferLoading(true)
    const isMember = transferPending.target_role === 'member' || !transferPending.target_role
    try {
      const { data, error } = await supabase.functions.invoke('request-facility-transfer', {
        body: {
          user_id: transferPending.user_id,
          email: transferPending.email,
          class_id: isMember ? (learnerClass || null) : null,
          target_role: transferPending.target_role || 'member',
        },
      })
      let parsed: any = data
      if (error) {
        try { parsed = await (error as any).context?.json?.() } catch { /* ignore */ }
        throw new Error(parsed?.error || error.message)
      }
      if (parsed?.error) throw new Error(parsed.error)
      const msg = parsed?.message || 'Transfer request sent.'
      if (isMember) {
        setLearnerMsg(msg)
      } else {
        setInviteMsg(msg)
      }
      setTransferPending(prev => prev ? { ...prev, sent: true } : null)
    } catch (err: any) {
      if (isMember) {
        setLearnerMsg(err.message || 'Failed to send transfer request.')
      } else {
        setInviteMsg(err.message || 'Failed to send transfer request.')
      }
    } finally {
      setTransferLoading(false)
    }
  }

  // ── Deactivate / reactivate a learner, releases / consumes a seat ──
  const toggleMember = async (m: MemberRow) => {
    setMemberBusy(m.id); setMemberMsg(null)
    try {
      const { data, error } = await supabase.functions.invoke('manage-member', {
        body: { user_id: m.id, action: m.is_active ? 'deactivate' : 'reactivate' },
      })
      if (error || data?.error) throw new Error((error as any)?.message || data?.error)
      setMembers(prev => prev.map(x => x.id === m.id ? { ...x, is_active: !m.is_active } : x))
      setMemberMsg(data?.message || null)
    } catch (e: any) {
      setMemberMsg(e.message || 'Failed to update member')
    } finally {
      setMemberBusy(null)
    }
  }

  // ── Resend magic link sign-in email to a member ──
  const resendInvite = async (m: MemberRow) => {
    setResendingId(m.id)
    setMemberMsg(null)
    try {
      const { data, error } = await supabase.functions.invoke('invite-student', {
        body: { email: m.email, resend: true },
      })
      if (error || data?.error) throw new Error((error as any)?.message || data?.error)
      setMemberMsg(`✓ Magic link invite resent to ${m.email}!`)
      setTimeout(() => setMemberMsg(null), 5000)
    } catch (e: any) {
      setMemberMsg(`Failed to resend: ${e.message}`)
    } finally {
      setResendingId(null)
    }
  }

  // ── Upload logo with automatic white background / corner removal ──
  const uploadLogo = async (file: File) => {
    if (!facilityId) return
    setLogoCleaning(true)
    try {
      const cleanFile = await cleanLogoFile(file)
      const path = `${facilityId}/logo.png`
      const { error } = await supabase.storage.from('university-branding').upload(path, cleanFile, { upsert: true, contentType: 'image/png' })
      if (error) { console.error('Logo upload failed:', error); return }
      const { data: urlData } = supabase.storage.from('university-branding').getPublicUrl(path)
      const cleanUrl = `${urlData.publicUrl}?t=${Date.now()}`
      setBranding(p => ({ ...p, logo_url: cleanUrl }))
      setSaveMsg('Logo uploaded with transparent background!')
      setTimeout(() => setSaveMsg(null), 3000)
    } catch (err: any) {
      console.error('Logo upload processing failed:', err)
    } finally {
      setLogoCleaning(false)
    }
  }

  // ── Auto-clean existing logo white background ──
  const handleAutoCleanExistingLogo = async () => {
    if (!facilityId || !branding.logo_url) return
    setLogoCleaning(true)
    try {
      const blob = await convertUrlToCleanPngBlob(branding.logo_url)
      if (!blob) {
        setSaveMsg('No white background detected')
        setTimeout(() => setSaveMsg(null), 3000)
        return
      }
      const cleanFile = new File([blob], 'logo.png', { type: 'image/png' })
      const path = `${facilityId}/logo.png`
      const { error } = await supabase.storage.from('university-branding').upload(path, cleanFile, { upsert: true, contentType: 'image/png' })
      if (error) throw error
      const { data: urlData } = supabase.storage.from('university-branding').getPublicUrl(path)
      const cleanUrl = `${urlData.publicUrl}?t=${Date.now()}`
      setBranding(p => ({ ...p, logo_url: cleanUrl }))
      setSaveMsg('Logo cleaned and saved transparently!')
      setTimeout(() => setSaveMsg(null), 4000)
    } catch (err: any) {
      console.error('Failed to clean logo:', err)
      setSaveMsg('Could not clean logo')
      setTimeout(() => setSaveMsg(null), 3000)
    } finally {
      setLogoCleaning(false)
    }
  }

    // ── Full course creation: catalog copy / community clone / custom shell.
  //    Mirrors useEducationAdmin.createClass (the hook itself drags a full
  //    data load — this console already runs its own load()).
  const createCourse = async (payload: CreateCoursePayload) => {
    if (!facilityId) return
    if (payload.origin !== 'community' && !payload.category_id) return
    setCreatingCourse(true); setCourseError(null)
    try {
      if (payload.origin === 'community' && payload.source_class_id) {
        const res = await cloneCommunityCourse(
          payload.source_class_id,
          { teacherId: user!.id, facilityId },
          payload.name
        )
        if (res.error) throw new Error(res.error)
      } else {
        let masterPlan: any = null
        let essayMode: string | null = null
        let curriculumTrack: string | null = null
        let subjectGroup: string | null = null
        let knowledgeContext: string | null = null
        let rowOrigin: 'master' | 'teacher_created' = 'teacher_created'
        let name = payload.name
        if (payload.source_class_id) {
          const { data: master, error: masterErr } = await supabase
            .from('classes')
            .select('module_plan, essay_mode, curriculum_track, subject_group, knowledge_context, name')
            .eq('id', payload.source_class_id)
            .maybeSingle()
          if (masterErr) throw masterErr
          if (!master) throw new Error('Master course not found.')
          masterPlan = master.module_plan
          essayMode = master.essay_mode
          curriculumTrack = master.curriculum_track
          subjectGroup = master.subject_group
          knowledgeContext = master.knowledge_context
          if (!name || name === (master as any).name) {
            name = `${(master as any).name}: ${branding.display_name || 'Course'}`
          }
          rowOrigin = 'master'
        }
        const { data: insertedClass, error } = await (supabase.from('classes') as any).insert({
          name: name ?? 'Untitled course',
          category_id: payload.category_id,
          description: payload.description ?? '',
          facility_id: facilityId,
          teacher_id: user?.id,
          owned_by_facility: facilityId,
          is_active: true,
          module_plan: masterPlan,
          essay_mode: essayMode,
          curriculum_track: curriculumTrack,
          subject_group: subjectGroup ?? 'General',
          knowledge_context: knowledgeContext,
          source_class_id: payload.source_class_id ?? null,
          origin: rowOrigin,
          is_community_published: false,
        }).select('id').maybeSingle()
        if (error) throw error

        if (payload.source_class_id && insertedClass?.id) {
          try {
            await supabase.rpc('copy_class_lesson_media', {
              p_source_class_id: payload.source_class_id,
              p_target_class_id: insertedClass.id
            })
          } catch (mediaErr) {
            console.warn('[UniversityBrandAdminPage] Failed to copy lesson media:', mediaErr)
          }
        }
      }
      setShowCreate(false)
      await load()
      // Generate flow: open the generator on the fresh shell — the user's
      // next step is knowledge + prompt, in context, not a hunt for the button
      if (payload.origin === 'generate' && !payload.source_class_id) {
        // fetch the new course id, then hand it to the generator modal
        const { data: newCourse } = await supabase
          .from('classes')
          .select('id, name')
          .eq('facility_id', facilityId)
          .eq('name', payload.name || 'Untitled course')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        if (newCourse) {
          setCourseName(newCourse.name)
          setNewCourseCategory(payload.category_id)
          setNewCourseId(newCourse.id)
          setNewCourseUseSearch(Boolean(payload.use_search))
          setCourseStep(true)
          setShowGen(true)
        }
      }
    } catch (err: any) {
      setCourseError(err.message || 'Failed to create the course.')
    } finally {
      setCreatingCourse(false)
    }
  }

  // ── Create the course shell FIRST, the generator's Publish writes
  // module_plan by class id and needs a real UUID from the start. ──
  const createCourseShell = async () => {
    if (!facilityId || !courseName.trim() || !newCourseCategory) return
    setCreatingCourse(true)
    setCourseError(null)
    try {
      const { data, error } = await supabase
        .from('classes')
        .insert({
          name: courseName.trim(),
          category_id: newCourseCategory,
          target_age: parseInt(newCourseAge) || null,
          teacher_id: user?.id,
          facility_id: facilityId,
          owned_by_facility: facilityId,
          delivery_mode: 'theory',
          origin: 'teacher_created',
          is_active: true,
          module_plan: [],
        } as any)
        .select('id')
        .single()
      if (error) throw error
      setNewCourseId(data.id)
      setCourseStep(true)
    } catch (err: any) {
      setCourseError(err.message || 'Failed to create the course.')
    } finally {
      setCreatingCourse(false)
    }
  }

  if (authLoading) {
    return (
      <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={28} color={BLUE} className="animate-spin" />
      </div>
    )
  }

  // ── Anonymous prospect: the landing's "Start Setup" lands HERE, not on a
  // sign-in wall. One form: account + organisation + facility in one call.
  if (!user) {
    return (
      <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', flexDirection: 'column' }}>
        <header style={{
          borderBottom: `1px solid ${BR}`,
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(6,6,12,0.92)',
          backdropFilter: 'blur(12px)',
        }}>
          <Link to="/platform" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: MU, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
            <ArrowLeft size={16} /> Back to Platform
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguagePicker compact />
            <Link to="/auth/login" style={{ color: BLUE, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
              Sign In
            </Link>
          </div>
        </header>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 460 }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ width: 64, height: 64, borderRadius: 20, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, margin: '0 auto 14px' }}>
                {org.emoji}
              </div>
              <h1 style={{ fontFamily: serif, fontSize: 26, fontWeight: 700, color: TX, margin: 0 }}>
                Create your {org.label}
              </h1>
              <p style={{ fontSize: 13, color: MU, margin: '8px 0 0' }}>
                {org.tagline}
              </p>
            </div>

            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: 28, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.organisation_name')}</label>
                <input type="text" value={wlOrgName} onChange={e => setWlOrgName(e.target.value)}
                  placeholder={ORG_NAME_PLACEHOLDERS[orgType || 'business'] || 'e.g. Your Organisation'}
                  style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.your_email')}</label>
                <input type="email" value={wlEmail} onChange={e => setWlEmail(e.target.value)}
                  placeholder="you@organisation.com"
                  style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Password</label>
                <input type="password" value={wlPassword} onChange={e => setWlPassword(e.target.value)}
                  placeholder={t('university.min_characters')}
                  onKeyDown={e => e.key === 'Enter' && handleOrgSignup()}
                  style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {wlError && <p style={{ fontSize: 12.5, color: RED, margin: 0 }}>{wlError}</p>}
              <button onClick={handleOrgSignup} disabled={wlLoading || !wlOrgName.trim() || !wlEmail.trim() || wlPassword.length < 8}
                style={{ padding: 14, borderRadius: 12, border: 'none', cursor: 'pointer', background: (wlOrgName.trim() && wlEmail.trim() && wlPassword.length >= 8) ? G : 'rgba(255,255,255,0.05)', color: (wlOrgName.trim() && wlEmail.trim() && wlPassword.length >= 8) ? M : MU, fontWeight: 800, fontSize: 14 }}>
                {wlLoading ? 'Creating…' : `Create ${org.label} & Continue`}
              </button>
              <p style={{ fontSize: 11, color: MU2, margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
                Already have an account? <Link to="/auth/login" style={{ color: BLUE, fontWeight: 700 }}>Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={28} color={BLUE} className="animate-spin" />
      </div>
    )
  }

  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'branding', label: 'Branding & Setup', icon: <Palette size={15} /> },
    { id: 'courses', label: org.courseLabel, icon: <BookOpen size={15} /> },
    { id: 'staff', label: org.staffLabel, icon: <Users size={15} /> },
    { id: 'learners', label: org.learnerLabel, icon: <GraduationCap size={15} /> },
    { id: 'billing', label: 'Billing & Seats', icon: <CreditCard size={15} /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={15} /> },
  ]

  const totalEnrolled = members.filter(m => m.is_active).length

  // Slug status indicator
  const slugBorderColor = slugStatus === 'available' ? GR
    : (slugStatus === 'taken' || slugStatus === 'invalid' || slugStatus === 'reserved') ? RED : BR

  const SlugRow = () => {
    if (!subdomainInput) return null
    const data: Record<SlugStatus, { icon: React.ReactNode; msg: string; color: string }> = {
      idle: { icon: null, msg: '', color: MU },
      checking: { icon: <Loader2 size={13} className="animate-spin" />, msg: 'Checking availability...', color: MU },
      available: { icon: <CheckCircle size={13} />, msg: subdomainInput === currentSlug ? 'Your current subdomain' : 'Available, claim it!', color: GR },
      taken: { icon: <XCircle size={13} />, msg: 'Already taken, try another', color: RED },
      invalid: { icon: <AlertCircle size={13} />, msg: 'Min 3 characters, letters and hyphens only', color: G },
      reserved: { icon: <AlertCircle size={13} />, msg: 'Reserved system name, choose another', color: G },
    }
    const { icon, msg, color } = data[slugStatus]
    if (!msg) return null
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <span style={{ color }}>{icon}</span>
        <span style={{ fontSize: 11.5, color, fontWeight: 600 }}>{msg}</span>
      </div>
    )
  }

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", overflowX: 'hidden', boxSizing: 'border-box' }}>
      <SEO title={`${branding.display_name || 'Admin'} : Platform Setup`} noindex />

      {/* ─── STREAMLINED ADMIN HEADER ─── */}
      <header style={{
        background: `linear-gradient(135deg, ${branding.primary_color}25 0%, #06060c 70%)`,
        borderBottom: `1px solid ${BR}`,
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(16px)',
      }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '12px clamp(12px,3vw,20px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>

            {/* Left: Quick Back + Org Logo/Emoji + Name & Role */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <Link
                to="/education-admin"
                title="Back to Teaching Hub"
                style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: 'rgba(255,255,255,0.06)', border: `1px solid ${BR}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: MU, textDecoration: 'none', flexShrink: 0,
                }}
              >
                <ArrowLeft size={15} />
              </Link>

              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: 'rgba(255,255,255,0.06)', border: `1px solid ${BR}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, overflow: 'hidden'
              }}>
                {branding.logo_url ? (
                  <BrandLogo src={branding.logo_url} alt={branding.display_name} style={{ width: 38, height: 38, objectFit: 'contain' }} fallback={<span style={{ fontSize: 20 }}>{org.emoji}</span>} />
                ) : (
                  <span style={{ fontSize: 20 }}>{org.emoji}</span>
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h1 style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, color: TX, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {branding.display_name || `Your ${org.label}`}
                  </h1>
                  <span style={{
                    fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em',
                    background: 'rgba(14,165,233,0.15)', color: BLUE, border: '1px solid rgba(14,165,233,0.3)',
                    padding: '2px 7px', borderRadius: 999, flexShrink: 0
                  }}>
                    Console
                  </span>
                </div>
                <p style={{ fontSize: 11, color: MU, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {org.headerSub}
                </p>
              </div>
            </div>

            {/* Right: Desktop Actions */}
            <div className="hidden md:flex" style={{ alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Quick EduAdmin Button */}
              <Link to="/education-admin"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
                  background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.4)',
                  color: G, fontSize: 12, fontWeight: 800, textDecoration: 'none', transition: 'all 0.15s'
                }}>
                <BarChart3 size={13} /> Edu Admin
              </Link>

              {/* Shrunk Subdomain Button: "Check site" */}
              {branding.subdomain && (
                <a href={`https://${branding.subdomain}.stillwatersacademy.com`} target="_blank" rel="noopener noreferrer"
                  title={`Visit live portal: ${branding.subdomain}.stillwatersacademy.com`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 10,
                    background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.35)',
                    color: BLUE, fontSize: 12, fontWeight: 700, textDecoration: 'none', transition: 'all 0.15s'
                  }}>
                  <Globe size={13} /> Check site <ExternalLink size={11} style={{ opacity: 0.7 }} />
                </a>
              )}

              {/* Verification Standards */}
              <Link to="/verification"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 10,
                  border: `1px solid ${BR}`, color: MU, fontSize: 12, fontWeight: 600, textDecoration: 'none'
                }}>
                <Shield size={13} /> Standards
              </Link>

              {/* Language Picker */}
              <LanguagePicker compact />
            </div>

            {/* Right: Mobile Hamburger Toggle */}
            <div className="flex md:hidden" style={{ alignItems: 'center', gap: 8 }}>
              <LanguagePicker compact />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle navigation menu"
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(255,255,255,0.06)', border: `1px solid ${BR}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: TX, cursor: 'pointer'
                }}>
                {mobileMenuOpen ? <X size={17} /> : <Menu size={17} />}
              </button>
            </div>
          </div>

          {/* Mobile Dropdown Panel */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden', paddingTop: 12, marginTop: 12, borderTop: `1px solid ${BR}` }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 6 }}>
                  {/* Quick EduAdmin Button */}
                  <Link
                    to="/education-admin"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10,
                      background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.3)',
                      color: G, fontSize: 13, fontWeight: 700, textDecoration: 'none'
                    }}
                  >
                    <BarChart3 size={15} /> Teaching Hub / Edu Admin
                  </Link>

                  {/* Shrunk Subdomain Button */}
                  {branding.subdomain && (
                    <a
                      href={`https://${branding.subdomain}.stillwatersacademy.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10,
                        background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.3)',
                        color: BLUE, fontSize: 13, fontWeight: 700, textDecoration: 'none'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Globe size={15} /> Check live site ({branding.subdomain})
                      </span>
                      <ExternalLink size={13} />
                    </a>
                  )}

                  {/* Verification Standards */}
                  <Link
                    to="/verification"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`,
                      color: MU, fontSize: 13, fontWeight: 600, textDecoration: 'none'
                    }}
                  >
                    <Shield size={15} /> Verification Standards
                  </Link>

                  {/* Back to main dashboard */}
                  <Link
                    to="/learn"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`,
                      color: MU, fontSize: 13, fontWeight: 600, textDecoration: 'none'
                    }}
                  >
                    <ArrowLeft size={15} /> Return to Learning
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 'clamp(20px,4vw,32px) clamp(12px,3vw,24px) 80px', boxSizing: 'border-box', width: '100%' }}>

        {/* ─── STATS ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 12, marginBottom: 28 }}>
          {[
            { label: org.courseLabel, value: courses.length, color: BLUE },
            { label: org.staffLabel, value: staff.filter(s => s.is_active).length, color: G },
            { label: `Enrolled ${org.learnerLabel.toLowerCase()}`, value: totalEnrolled, color: GR },
            { label: 'Credentials issued', value: '—', color: G },
          ].map((s, i) => (
            <div key={i} style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 16, padding: '16px 18px' }}>
              <p style={{ fontSize: 24, fontWeight: 800, color: s.color, margin: 0 }}>{s.value}</p>
              <p style={{ fontSize: 10, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '6px 0 0' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* ─── TABS ─── */}
        <div
          className="scrollbar-hide"
          style={{
            display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 4, marginBottom: 24,
            overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', boxSizing: 'border-box', WebkitOverflowScrolling: 'touch'
          }}
        >
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ flexShrink: 0, padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: activeTab === tab.id ? 'rgba(255,255,255,0.08)' : 'transparent', color: activeTab === tab.id ? TX : MU, fontWeight: activeTab === tab.id ? 800 : 600, fontSize: 13, whiteSpace: 'nowrap' }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ─── BRANDING TAB ─── */}
        {activeTab === 'branding' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Org type */}
            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 28px' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: '0 0 4px' }}>{t('university.what_type_of_organisation_are_you')}</h2>
              <p style={{ fontSize: 12.5, color: MU, margin: '0 0 18px', lineHeight: 1.6 }}>
                Sets terminology across your platform, {org.staffLabel.toLowerCase()}, {org.learnerLabel.toLowerCase()}, {org.courseLabel.toLowerCase()}.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', gap: 10 }}>
                {ORG_TYPES.map(o => {
                  const sel = orgType === o.id
                  return (
                    <button key={o.id}
                      onClick={() => { setOrgType(o.id); setBranding(p => ({ ...p, org_type: o.id })) }}
                      style={{ padding: '14px 16px', borderRadius: 14, border: `2px solid ${sel ? BLUE : BR}`, background: sel ? 'rgba(14,165,233,0.08)' : 'rgba(255,255,255,0.02)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                      <div style={{ fontSize: 22, marginBottom: 6 }}>{o.emoji}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: sel ? BLUE : TX, marginBottom: 4 }}>{o.label}</div>
                      <div style={{ fontSize: 11, color: MU2, lineHeight: 1.4 }}>{o.tagline}</div>
                      {sel && <div style={{ marginTop: 8, fontSize: 10.5, fontWeight: 700, color: BLUE, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Selected</div>}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Subdomain */}
            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 28px' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: '0 0 4px' }}>{t('university.your_branded_address')}</h2>
              <p style={{ fontSize: 12.5, color: MU, margin: '0 0 18px', lineHeight: 1.6 }}>
                Your platform lives at <strong style={{ color: TX }}>yourname.stillwatersacademy.com</strong>, instant wildcard DNS on Vercel. Subdomains are unique, first-come first-served.
              </p>
              {slugEditing ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: `2px solid ${slugBorderColor}`, borderRadius: 14, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                    <input
                      type="text" value={subdomainInput}
                      onChange={e => handleSlugChange(e.target.value)}
                      placeholder="your-org-name" maxLength={30}
                      style={{ flex: 1, padding: '14px 18px', background: 'transparent', border: 'none', color: TX, fontSize: 15, fontWeight: 700, outline: 'none', fontFamily: 'monospace' }}
                    />
                    <span style={{ padding: '14px 18px 14px 0', fontSize: 13, color: MU, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      .stillwatersacademy.com
                    </span>
                  </div>
                  <SlugRow />
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '16px 18px', background: 'rgba(74,222,128,0.06)', border: '2px solid rgba(74,222,128,0.35)', borderRadius: 14 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: TX, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      🌐 {currentSlug}.stillwatersacademy.com
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 11.5, color: GR, fontWeight: 700 }}>
                      ✓ Claimed &amp; live, this is your permanent address
                    </p>
                  </div>
                  <button onClick={() => { setSlugEditing(true); setSlugStatus('idle') }}
                    style={{ padding: '9px 18px', borderRadius: 10, border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.05)', color: MU, fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                    Change
                  </button>
                </div>
              )}
              {currentSlug && currentSlug === subdomainInput && (
                <div style={{ marginTop: 10, padding: '12px 16px', borderRadius: 12, background: 'rgba(14,165,233,0.07)', border: '1px solid rgba(14,165,233,0.2)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Globe size={14} color={BLUE} />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 700, color: BLUE, margin: 0 }}>Live: {currentSlug}.stillwatersacademy.com</p>
                    <p style={{ fontSize: 11, color: MU2, margin: '2px 0 0' }}>Resolves automatically via Vercel wildcard.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Visual identity */}
            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 28px' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: '0 0 4px' }}>{t('university.visual_identity')}</h2>
              <p style={{ fontSize: 12.5, color: MU, margin: '0 0 18px', lineHeight: 1.6 }}>
                Appears on credentials, certificates, and verification pages, alongside the SWA evidence seal. Upload a PNG with a transparent background and rounded shape for the cleanest result.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {/* Logo */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Logo</label>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ width: 64, height: 64, borderRadius: 16, border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 2, flexShrink: 0 }}>
                      {branding.logo_url ? (
                        <BrandLogo src={branding.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} fallback={<span style={{ fontSize: 28 }}>{org.emoji}</span>} />
                      ) : (
                        <span style={{ fontSize: 28 }}>{org.emoji}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: `1px solid ${BR}`, cursor: logoCleaning ? 'not-allowed' : 'pointer', color: G, fontWeight: 700, fontSize: 12, opacity: logoCleaning ? 0.6 : 1 }}>
                        {logoCleaning ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {logoCleaning ? 'Processing logo…' : 'Upload logo (round, square, or squircle)'}
                        <input type="file" accept="image/*" style={{ display: 'none' }} disabled={logoCleaning} onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
                      </label>
                      {branding.logo_url && (
                        <button
                          type="button"
                          onClick={handleAutoCleanExistingLogo}
                          disabled={logoCleaning}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.04)', cursor: 'pointer', color: TX, fontWeight: 700, fontSize: 12, opacity: logoCleaning ? 0.6 : 1 }}
                          title="Removes any white background or corners from this logo and saves it transparently"
                        >
                          <Sparkles size={13} color={G} /> {logoCleaning ? 'Cleaning…' : 'Clean & save transparent'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.display_name')}</label>
                  <input
                    type="text" value={branding.display_name}
                    onChange={e => setBranding(p => ({ ...p, display_name: e.target.value }))}
                    placeholder={`e.g. Springfield ${org.label}`}
                    style={{ width: '100%', padding: '14px 18px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.03)', color: TX, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Colors */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.primary_colour')}</label>
                    <input type="color" value={branding.primary_color} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} style={{ width: '100%', height: 48, borderRadius: 12, border: `2px solid ${BR}`, background: 'transparent', cursor: 'pointer' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.accent_colour')}</label>
                    <input type="color" value={branding.secondary_color} onChange={e => setBranding(p => ({ ...p, secondary_color: e.target.value }))} style={{ width: '100%', height: 48, borderRadius: 12, border: `2px solid ${BR}`, background: 'transparent', cursor: 'pointer' }} />
                  </div>
                </div>

                {/* Curated palette — one-click picks that actually show on dark surfaces.
                    Dark hex defaults read as "the colours don't work" against the console. */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.popular_colours')}</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      { p: '#1E3A8A', s: '#3B82F6', name: 'Corporate blue' },
                      { p: '#14532D', s: '#22C55E', name: 'Growth green' },
                      { p: '#7C2D12', s: '#F97316', name: 'Construction orange' },
                      { p: '#581C87', s: '#A855F7', name: 'Creative violet' },
                      { p: '#0C4A6E', s: '#0EA5E9', name: 'Sky' },
                      { p: '#713F12', s: '#EAB308', name: 'Gold' },
                    ].map(sw => (
                      <button key={sw.name} title={`${sw.name} (${sw.p} / ${sw.s})`}
                        onClick={() => setBranding(p => ({ ...p, primary_color: sw.p, secondary_color: sw.s }))}
                        style={{ width: 44, height: 44, borderRadius: 12, border: `2px solid ${BR}`, cursor: 'pointer', overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
                        <span style={{ flex: 1, background: sw.p, display: 'block' }} />
                        <span style={{ flex: 1, background: sw.s, display: 'block' }} />
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                    <p style={{ fontSize: 11, color: MU2, margin: 0, lineHeight: 1.5, flex: 1, minWidth: 200 }}>
                      Pick a pair, or set your exact brand colours with the selectors above. Avoid near-black primaries, they vanish on the dark portal.
                    </p>
                    <button
                      onClick={() => setBranding(p => ({ ...p, primary_color: '#162e22', secondary_color: '#F97316' }))}
                      title="Reset to the recommended default colours"
                      style={{ padding: '7px 14px', borderRadius: 10, border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: MU, fontWeight: 700, fontSize: 11.5, cursor: 'pointer', flexShrink: 0 }}
                    >
                      ↺ Reset to default
                    </button>
                  </div>
                </div>

                {/* Live preview, the learner-facing hero, exactly as it will render */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.live_preview_learner_view')}</label>
                  <div style={{ background: `linear-gradient(135deg, ${branding.primary_color}, #06060c)`, borderRadius: 16, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, border: `1px solid ${BR}` }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {branding.logo_url ? (
                        <BrandLogo src={branding.logo_url} alt={branding.display_name} style={{ width: 44, height: 44, objectFit: 'contain' }} fallback={<div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{org.emoji}</div>} />
                      ) : (
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{org.emoji}</div>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, color: TX, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {branding.display_name || `Your ${org.label}`}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                        Learner dashboard header · accent colour below
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 12px', borderRadius: 999, background: branding.secondary_color, color: '#06060c' }}>Sign in</span>
                        <span style={{ width: 12, height: 4, borderRadius: 2, background: branding.secondary_color }} />
                        <span style={{ width: 44, height: 4, borderRadius: 2, background: branding.secondary_color, opacity: 0.55 }} />
                      </div>
                    </div>
                  </div>
                  <p style={{ fontSize: 11, color: MU2, margin: '8px 0 0', lineHeight: 1.5 }}>
                    This is how {branding.display_name || 'your organisation'} will appear on the learner dashboard, login page, and front door. The full preview updates on every change, live.
                  </p>
                </div>
              </div>
            </div>

            {/* Save */}
            <button onClick={save} disabled={saving || !canSave}
              style={{ padding: '15px 36px', borderRadius: 14, border: 'none', cursor: canSave ? 'pointer' : 'not-allowed', background: canSave ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)', color: canSave ? M : MU, fontWeight: 800, fontSize: 15, alignSelf: 'flex-start', transition: 'all 0.2s' }}>
              {saving
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={14} className="animate-spin" /> Saving...</span>
                : 'Save & go live'}
            </button>
            {saveMsg && (
              <p style={{ fontSize: 12.5, color: saveMsg.startsWith('Saved') ? GR : RED, margin: 0, fontWeight: 600 }}>{saveMsg}</p>
            )}
          </div>
        )}

        {/* ─── COURSES TAB ─── */}
        {activeTab === 'courses' && (
          <div>
            {courseMsg && (
              <div style={{
                marginBottom: 16, padding: '12px 18px', borderRadius: 12,
                background: courseMsg.type === 'success' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                border: `1px solid ${courseMsg.type === 'success' ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
                color: courseMsg.type === 'success' ? '#10b981' : '#f87171',
                fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10
              }}>
                <span style={{ fontSize: 16 }}>{courseMsg.type === 'success' ? '✓' : '⚠️'}</span>
                <span>{courseMsg.text}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: 0 }}>Your {org.courseLabel}</h2>
                <p style={{ fontSize: 12.5, color: MU, margin: '4px 0 0' }}>
                  Theory {org.courseLabel.toLowerCase()} run on the SWA engine, AI-tutored, server-verified.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => { setCourseError(null); setShowCreate(true) }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 12, border: 'none', cursor: 'pointer', background: G, color: M, fontWeight: 800, fontSize: 13 }}>
                  <Plus size={14} /> Add {org.courseSingular}
                </button>
              </div>
            </div>

            {/* Catalog Streams — which course streams this org's pickers may pull from */}
            <CatalogStreamsCard facilityId={facilityId} />

            {courses.length === 0 ? (
              <div style={{ background: CARD, border: `1px dashed ${BR}`, borderRadius: 20, padding: '48px 24px', textAlign: 'center' }}>
                <BookOpen size={32} color={MU2} style={{ margin: '0 auto 12px', display: 'block' }} />
                <p style={{ color: MU, fontSize: 14, fontWeight: 600 }}>No {org.courseLabel.toLowerCase()} yet</p>
                <p style={{ color: MU2, fontSize: 12.5, marginTop: 4 }}>{t('university.pick_from_the_master_catalog_or')}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {courses.map(course => {
                  const isAll = course.enrollment_mode === 'all'
                  const isTargeted = course.enrollment_mode === 'targeted'
                  const activeMemberCount = members.filter(m => m.is_active).length
                  const assignedMembers = isTargeted
                    ? members.filter(m => course.enrolled_student_ids?.includes(m.id))
                    : []
                  const isRosterExpanded = expandedRosterCourseId === course.id

                  return (
                    <div
                      key={course.id}
                      onClick={() => setEditingCourse(course)}
                      style={{
                        background: CARD, border: `1px solid ${BR}`, borderRadius: 16,
                        padding: 'clamp(12px, 3.5vw, 18px)', display: 'flex', flexDirection: 'column', gap: 12,
                        cursor: 'pointer', transition: 'all 0.2s ease', width: '100%', boxSizing: 'border-box'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = 'rgba(212,168,67,0.45)'
                        e.currentTarget.style.background = 'rgba(255,255,255,0.035)'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = BR
                        e.currentTarget.style.background = CARD
                      }}
                    >
                      {/* Top row: Icon + Course Details */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}>
                        <div style={{ width: 'clamp(36px, 9vw, 42px)', height: 'clamp(36px, 9vw, 42px)', minWidth: 36, borderRadius: 12, background: course.delivery_mode === 'theory' ? 'rgba(14,165,233,0.1)' : 'rgba(212,168,67,0.1)', color: course.delivery_mode === 'theory' ? BLUE : G, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                          <BookOpen size={18} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <p style={{ fontSize: 'clamp(14px, 3.8vw, 15px)', fontWeight: 700, color: TX, margin: 0, lineHeight: 1.3, wordBreak: 'break-word' }}>{course.name}</p>
                            <span style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 999, background: course.delivery_mode === 'theory' ? 'rgba(14,165,233,0.12)' : 'rgba(212,168,67,0.12)', color: course.delivery_mode === 'theory' ? BLUE : G, flexShrink: 0 }}>
                              {course.delivery_mode}
                            </span>
                            {!course.is_published ? (
                              <span style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 999, background: 'rgba(148,163,184,0.15)', color: '#94a3b8', border: '1px solid rgba(148,163,184,0.3)', display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#94a3b8' }} /> Draft: Invisible to learners
                              </span>
                            ) : isTargeted ? (
                              <span style={{
                                fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 999,
                                background: assignedMembers.length === 0 ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
                                color: assignedMembers.length === 0 ? '#f59e0b' : '#10b981',
                                border: `1px solid ${assignedMembers.length === 0 ? 'rgba(245,158,11,0.35)' : 'rgba(16,185,129,0.3)'}`,
                                display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0
                              }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: assignedMembers.length === 0 ? '#f59e0b' : '#10b981' }} />
                                {assignedMembers.length === 0 ? 'Live: Targeted (0 assigned · invisible)' : `Live: Targeted (${assignedMembers.length} assigned)`}
                              </span>
                            ) : (
                              <span style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 999, background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} /> Live: entire organisation
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: 'clamp(11.5px, 3.2vw, 12px)', color: MU, margin: '6px 0 0', lineHeight: 1.4, wordBreak: 'break-word' }}>
                            {course.module_count ?? 0} modules · {course.lesson_count ?? 0} lessons · {isAll ? `All ${activeMemberCount} ${org.learnerLabel.toLowerCase()} (Entire org)` : `${assignedMembers.length} assigned ${assignedMembers.length === 1 ? org.learnerSingular.toLowerCase() : org.learnerLabel.toLowerCase()}`}
                          </p>

                          {/* Quick Targeted Roster Expand Button */}
                          {isTargeted && (
                            <div style={{ marginTop: 6 }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setExpandedRosterCourseId(isRosterExpanded ? null : course.id)
                                }}
                                style={{
                                  background: isRosterExpanded ? 'rgba(245,158,11,0.18)' : 'rgba(255,255,255,0.06)',
                                  border: `1px solid ${isRosterExpanded ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.12)'}`,
                                  color: isRosterExpanded ? '#f59e0b' : TX,
                                  padding: '5px 11px', borderRadius: 8, fontSize: 11.5, fontWeight: 700,
                                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                                  minHeight: 28, transition: 'all 0.15s ease'
                                }}
                              >
                                <Users size={12} />
                                <span>Targeted Roster ({assignedMembers.length})</span>
                                <ChevronDown size={12} style={{ transform: isRosterExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expandable Targeted Learner List */}
                      {isTargeted && isRosterExpanded && (
                        <div
                          onClick={e => e.stopPropagation()}
                          style={{
                            padding: 'clamp(10px, 3vw, 14px)', borderRadius: 12, background: 'rgba(0,0,0,0.35)',
                            border: '1px solid rgba(255,255,255,0.08)', cursor: 'default', width: '100%', boxSizing: 'border-box'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                            <span style={{ fontSize: 11.5, fontWeight: 800, color: TX, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Users size={13} color={G} /> Assigned Learners ({assignedMembers.length})
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingCourse(course)
                                setEditTab('access')
                              }}
                              style={{
                                padding: '5px 11px', borderRadius: 8, border: `1px solid ${G}`,
                                background: 'rgba(212,168,67,0.15)', color: G, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0
                              }}
                            >
                              Manage Access &amp; Roster →
                            </button>
                          </div>

                          {assignedMembers.length === 0 ? (
                            <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b', fontSize: 12, lineHeight: 1.5 }}>
                              ⚠️ No learners are currently assigned to this targeted course. Even though it is marked Live, <strong>nobody can see or take it</strong> until you assign learners.
                              <div style={{ marginTop: 8 }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingCourse(course)
                                    setEditTab('access')
                                  }}
                                  style={{
                                    background: '#f59e0b', color: '#000', border: 'none', padding: '6px 14px',
                                    borderRadius: 6, fontSize: 11.5, fontWeight: 800, cursor: 'pointer'
                                  }}
                                >
                                  Assign Learners Now
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, width: '100%' }}>
                              {assignedMembers.map(m => (
                                <span
                                  key={m.id}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '4px 9px', borderRadius: 8,
                                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                                    fontSize: 11.5, color: '#ffffff', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box'
                                  }}
                                >
                                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 800, flexShrink: 0 }}>
                                    {(m.name || m.email).charAt(0).toUpperCase()}
                                  </span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                    {m.name || m.email}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Bottom row: Dedicated Action Buttons with clean responsive wrapping */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation()
                            await togglePublish(course.id, !course.is_published)
                          }}
                          disabled={publishingId === course.id}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '8px 13px', borderRadius: 10,
                            background: course.is_published ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.15)',
                            border: course.is_published ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(16,185,129,0.4)',
                            color: course.is_published ? '#f87171' : '#10b981',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            opacity: publishingId === course.id ? 0.6 : 1,
                            flex: '1 1 auto', minHeight: 36, whiteSpace: 'nowrap'
                          }}
                          title={course.is_published ? 'Unpublish course from learners' : 'Publish course to all learners'}
                        >
                          {publishingId === course.id ? (
                            <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : course.is_published ? (
                            <>
                              <EyeOff size={13} /> Unpublish
                            </>
                          ) : (
                            <>
                              <Eye size={13} /> Publish
                            </>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingCourse(course)
                            setEditTab('access')
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '8px 13px', borderRadius: 10,
                            background: isTargeted ? 'rgba(245,158,11,0.12)' : 'rgba(14,165,233,0.12)',
                            border: `1px solid ${isTargeted ? 'rgba(245,158,11,0.35)' : 'rgba(14,165,233,0.35)'}`,
                            color: isTargeted ? '#f59e0b' : '#38bdf8',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            flex: '1 1 auto', minHeight: 36, whiteSpace: 'nowrap'
                          }}
                          title="Manage learner enrollment access (Whole Org vs Targeted)"
                        >
                          <Users size={13} /> {isTargeted ? `Access: Targeted (${assignedMembers.length})` : 'Access: Whole Org'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingCourse(course)
                            setEditTab('syllabus')
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '8px 13px', borderRadius: 10,
                            background: 'rgba(212,168,67,0.15)', border: `1px solid ${G}`,
                            color: G, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            flex: '1 1 auto', minHeight: 36, whiteSpace: 'nowrap'
                          }}
                        >
                          <Settings size={13} /> Edit Course
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/education-admin?class=${course.id}`)
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            padding: '8px 12px', borderRadius: 10,
                            background: 'rgba(255,255,255,0.06)', border: `1px solid ${BR}`,
                            color: MU, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            flexShrink: 0, minHeight: 36
                          }}
                          title="Open in full Teaching Dashboard"
                        >
                          <ExternalLink size={13} />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Course Detail & Editor Modal */}
            <AnimatePresence>
              {editingCourse && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,12,0.85)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(6px, 2vw, 16px)', boxSizing: 'border-box' }}
                  onClick={() => setEditingCourse(null)}>
                  <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                    onClick={e => e.stopPropagation()}
                    style={{ background: '#0e0e17', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'clamp(14px, 3vw, 20px)', width: '100%', maxWidth: 840, maxHeight: '94dvh', overflowY: 'auto', padding: 'clamp(12px, 3.2vw, 24px)', color: '#ffffff', boxShadow: '0 25px 60px rgba(0,0,0,0.8)', boxSizing: 'border-box' }}>
                    
                    {/* Modal Header */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      paddingBottom: 14,
                      marginBottom: 16,
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      {/* Top Bar: Delivery Badges + Action Buttons */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        width: '100%',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 9px', borderRadius: 20, background: 'rgba(14,165,233,0.12)', color: '#0ea5e9', flexShrink: 0 }}>
                            {editingCourse.delivery_mode}
                          </span>
                          {editingCourse.is_published && (
                            <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, background: 'rgba(16,185,129,0.15)', color: '#34d399', flexShrink: 0 }}>
                              Live
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexShrink: 0, marginLeft: 'auto' }}>
                          <button
                            onClick={async () => {
                              await togglePublish(editingCourse.id, !editingCourse.is_published)
                            }}
                            disabled={publishingId === editingCourse.id}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '6px 11px', borderRadius: 8,
                              background: editingCourse.is_published ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.18)',
                              border: editingCourse.is_published ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(16,185,129,0.45)',
                              color: editingCourse.is_published ? '#f87171' : '#10b981',
                              fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                              opacity: publishingId === editingCourse.id ? 0.6 : 1,
                              whiteSpace: 'nowrap', minHeight: 32
                            }}
                          >
                            {publishingId === editingCourse.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : editingCourse.is_published ? (
                              <>
                                <EyeOff size={12} /> Unpublish
                              </>
                            ) : (
                              <>
                                <Eye size={12} /> Publish Course
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => navigate(`/education-admin?class=${editingCourse.id}`)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#ffffff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 32 }}
                            title="Open in full Teaching Dashboard"
                          >
                            <ExternalLink size={12} /> Full Dashboard
                          </button>
                          <button
                            onClick={() => setEditingCourse(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.55)', padding: '4px', minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
                            title="Close"
                          >
                            <X size={20} />
                          </button>
                        </div>
                      </div>

                      {/* Course Title (Full Width) */}
                      <div style={{ width: '100%', minWidth: 0 }}>
                        <input
                          value={editCourseName}
                          onChange={e => setEditCourseName(e.target.value)}
                          onBlur={(e) => {
                            e.currentTarget.style.borderBottomColor = 'transparent'
                            if (editCourseName.trim() && editCourseName.trim() !== editingCourse.name) saveCourseSettings()
                          }}
                          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                          title="Click to rename course"
                          style={{
                            fontSize: 'clamp(16px, 4.5vw, 22px)', fontWeight: 800, color: '#ffffff', background: 'transparent',
                            border: 'none', borderBottom: '2px solid transparent', outline: 'none',
                            padding: '2px 0', borderRadius: 4, width: '100%', minWidth: 0,
                            cursor: 'text', transition: 'border-color 0.15s', boxSizing: 'border-box'
                          }}
                          onFocus={e => { e.currentTarget.style.borderBottomColor = '#0ea5e9' }}
                        />
                        <p style={{ fontSize: 'clamp(11px, 3vw, 11.5px)', color: 'rgba(255,255,255,0.45)', margin: '4px 0 0', lineHeight: 1.4, wordBreak: 'break-word' }}>
                          Click name above to rename · {editingCourse.module_count ?? 0} modules · {editingCourse.lesson_count ?? 0} lessons · {editingCourse.enrolled_count} {org.learnerLabel.toLowerCase()}
                        </p>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div
                      className="scrollbar-hide"
                      style={{
                        display: 'flex', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.08)',
                        paddingBottom: 10, marginBottom: 18, overflowX: 'auto', width: '100%',
                        scrollbarWidth: 'none', msOverflowStyle: 'none', boxSizing: 'border-box',
                        WebkitOverflowScrolling: 'touch'
                      }}
                    >
                      <button
                        onClick={() => setEditTab('syllabus')}
                        style={{ padding: '7px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: editTab === 'syllabus' ? '#0ea5e9' : 'rgba(255,255,255,0.05)', color: editTab === 'syllabus' ? '#ffffff' : 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0, minHeight: 34 }}
                      >
                        <BookOpen size={14} /> Syllabus &amp; Lessons
                      </button>
                      <button
                        onClick={() => setEditTab('knowledge')}
                        style={{ padding: '7px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: editTab === 'knowledge' ? '#0ea5e9' : 'rgba(255,255,255,0.05)', color: editTab === 'knowledge' ? '#ffffff' : 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0, minHeight: 34 }}
                      >
                        <Upload size={14} /> Knowledge &amp; Docs
                      </button>
                      <button
                        onClick={() => setEditTab('settings')}
                        style={{ padding: '7px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: editTab === 'settings' ? '#0ea5e9' : 'rgba(255,255,255,0.05)', color: editTab === 'settings' ? '#ffffff' : 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0, minHeight: 34 }}
                      >
                        <Settings size={14} /> Course Settings
                      </button>
                      <button
                        onClick={() => setEditTab('access')}
                        style={{ padding: '7px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: editTab === 'access' ? '#0ea5e9' : 'rgba(255,255,255,0.05)', color: editTab === 'access' ? '#ffffff' : 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0, minHeight: 34 }}
                      >
                        <Users size={14} /> Access &amp; Learners {editingCourse.enrollment_mode === 'targeted' ? `(Targeted: ${enrolledStudentIds.length})` : '(All)'}
                      </button>
                    </div>

                    {/* Tab Content */}
                    {editTab === 'syllabus' && (
                      editLoadingPlan ? (
                        <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.55)' }}>
                          <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading syllabus…
                        </div>
                      ) : (
                        <SyllabusGenerator
                          classId={editingCourse.id}
                          targetAge={18}
                          subjectName={editingCourse.name}
                          existingPlan={editingPlan}
                          onSyllabusSaved={(plan) => { setEditingPlan(plan); load() }}
                        />
                      )
                    )}

                    {editTab === 'knowledge' && (
                      <KnowledgeUploader
                        classId={editingCourse.id}
                        onUpdate={() => load()}
                      />
                    )}

                    {editTab === 'settings' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>{t('university.course_name')}</label>
                          <input
                            type="text"
                            value={editCourseName}
                            onChange={e => setEditCourseName(e.target.value)}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#ffffff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>{t('university.delivery_mode')}</label>
                          <select
                            value={editDeliveryMode}
                            onChange={e => setEditDeliveryMode(e.target.value as any)}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: '#13131f', color: '#ffffff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                          >
                            <option value="theory">{t('university.theory_ai_tutored_quizzed')}</option>
                            <option value="practical">Practical</option>
                            <option value="hybrid">Hybrid</option>
                          </select>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap', gap: 10 }}>
                          <button
                            onClick={deleteCourse}
                            style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                          >
                            <Trash2 size={14} /> Delete Course
                          </button>
                          <button
                            onClick={saveCourseSettings}
                            disabled={savingSettings || !editCourseName.trim()}
                            style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#0ea5e9', color: '#ffffff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                          >
                            {savingSettings ? 'Saving…' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    )}

                    {editTab === 'access' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#ffffff', marginBottom: 6 }}>
                            Who can view &amp; take this {org.courseSingular.toLowerCase()}?
                          </label>
                          <p style={{ fontSize: 'clamp(11.5px, 3vw, 12px)', color: 'rgba(255,255,255,0.5)', margin: '0 0 14px', lineHeight: 1.5 }}>
                            Choose whether this course is open to the entire organisation or strictly restricted to selected individuals (e.g. specialized machinery training, promotions, discreet PIP).
                          </p>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 10, marginBottom: 16 }}>
                            <div
                              onClick={() => setEditEnrollmentMode('all')}
                              style={{
                                padding: 'clamp(12px, 3vw, 16px)', borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s ease',
                                border: `2px solid ${editEnrollmentMode === 'all' ? '#0ea5e9' : 'rgba(255,255,255,0.1)'}`,
                                background: editEnrollmentMode === 'all' ? 'rgba(14,165,233,0.12)' : 'rgba(255,255,255,0.02)',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 18 }}>👥</span>
                                <span style={{ fontWeight: 800, fontSize: 14, color: '#ffffff' }}>Entire Organisation</span>
                              </div>
                              <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.4 }}>
                                All active {org.learnerLabel.toLowerCase()} automatically see and enrol in this course when published.
                              </p>
                            </div>

                            <div
                              onClick={() => setEditEnrollmentMode('targeted')}
                              style={{
                                padding: 'clamp(12px, 3vw, 16px)', borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s ease',
                                border: `2px solid ${editEnrollmentMode === 'targeted' ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`,
                                background: editEnrollmentMode === 'targeted' ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.02)',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 18 }}>🎯</span>
                                <span style={{ fontWeight: 800, fontSize: 14, color: '#ffffff' }}>Targeted Learners Only</span>
                              </div>
                              <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.4 }}>
                                Strictly restricted. Only the learners you specifically select below will see this course in their portal.
                              </p>
                            </div>
                          </div>
                        </div>

                        {editEnrollmentMode === 'targeted' && (() => {
                          const activeMembers = members.filter(m => m.is_active)
                          return (
                            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 'clamp(12px, 3vw, 16px)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 700, fontSize: 13, color: '#ffffff' }}>Select Learners</span>
                                  <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>
                                    ({enrolledStudentIds.length} of {activeMembers.length} selected)
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button
                                    type="button"
                                    onClick={() => setEnrolledStudentIds(activeMembers.map(m => m.id))}
                                    style={{ padding: '5px 11px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#ffffff', fontSize: 11.5, cursor: 'pointer', minHeight: 30 }}
                                  >
                                    Select All
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEnrolledStudentIds([])}
                                    style={{ padding: '5px 11px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.6)', fontSize: 11.5, cursor: 'pointer', minHeight: 30 }}
                                  >
                                    Clear All
                                  </button>
                                </div>
                              </div>

                              <input
                                type="text"
                                placeholder="Search learners by name or email…"
                                value={enrolledSearch}
                                onChange={e => setEnrolledSearch(e.target.value)}
                                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: '#13131f', color: '#ffffff', fontSize: 13, outline: 'none', marginBottom: 10, boxSizing: 'border-box' }}
                              />

                              {loadingEnrolled ? (
                                <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                                  <Loader2 size={18} className="animate-spin" style={{ margin: '0 auto 6px', display: 'block' }} /> Loading roster…
                                </div>
                              ) : activeMembers.length === 0 ? (
                                <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>
                                  No active learners in this organisation yet. Add learners from the Learners tab first.
                                </p>
                              ) : (
                                <div style={{ maxHeight: 'clamp(180px, 35vh, 260px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 4 }}>
                                  {activeMembers
                                    .filter(m => !enrolledSearch.trim() || (m.name || '').toLowerCase().includes(enrolledSearch.toLowerCase()) || m.email.toLowerCase().includes(enrolledSearch.toLowerCase()))
                                    .map(m => {
                                      const isChecked = enrolledStudentIds.includes(m.id)
                                      return (
                                        <label
                                          key={m.id}
                                          style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                                            borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s',
                                            background: isChecked ? 'rgba(245,158,11,0.08)' : 'transparent',
                                            minHeight: 44, boxSizing: 'border-box'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={e => {
                                              if (e.target.checked) {
                                                setEnrolledStudentIds(prev => [...prev, m.id])
                                              } else {
                                                setEnrolledStudentIds(prev => prev.filter(id => id !== m.id))
                                              }
                                            }}
                                            style={{ width: 17, height: 17, accentColor: '#f59e0b', cursor: 'pointer', flexShrink: 0 }}
                                          />
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {m.name || 'Unnamed learner'}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {m.email}
                                            </div>
                                          </div>
                                        </label>
                                      )
                                    })}
                                </div>
                              )}
                            </div>
                          )
                        })()}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', width: '100%' }}>
                          <button
                            onClick={() => saveEnrollmentAccess(editEnrollmentMode, enrolledStudentIds)}
                            disabled={savingEnrolled || loadingEnrolled}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 22px',
                              borderRadius: 10, border: 'none', background: editEnrollmentMode === 'targeted' ? '#f59e0b' : '#0ea5e9',
                              color: '#ffffff', fontWeight: 800, fontSize: 13, cursor: 'pointer',
                              width: 'clamp(180px, 100%, 260px)', minHeight: 42,
                              opacity: (savingEnrolled || loadingEnrolled) ? 0.6 : 1,
                            }}
                          >
                            {savingEnrolled ? <Loader2 size={14} className="animate-spin" /> : null}
                            Save Access Settings
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Generator modal */}
            <AnimatePresence>
              {showGen && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,12,0.85)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                  onClick={() => { setShowGen(false); setCourseStep(false); setCourseName('') }}>
                  <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                    onClick={e => e.stopPropagation()}
                    style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '85vh', overflowY: 'auto', padding: 28, color: TX }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: 0 }}>
                        {courseStep ? `Syllabus: ${courseName}` : `Name your ${org.courseSingular.toLowerCase()}`}
                      </h3>
                      <button onClick={() => { setShowGen(false); setCourseStep(false); setCourseName('') }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MU }}>
                        <X size={20} />
                      </button>
                    </div>
                    {!courseStep ? (
                      <>
                        <p style={{ fontSize: 12.5, color: MU, marginBottom: 16, lineHeight: 1.6 }}>
                          Name your {org.courseSingular.toLowerCase()}, pick its subject area, then generate the syllabus into it.
                        </p>
                        <input type="text" value={courseName} onChange={e => setCourseName(e.target.value)}
                          placeholder="e.g. Introduction to Organic Chemistry"
                          style={{ width: '100%', padding: '14px 18px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
                          onKeyDown={e => e.key === 'Enter' && courseName.trim() && newCourseCategory && createCourseShell()} />
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 12 }}>
                          <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.subject_area')}</label>
                            <select value={newCourseCategory} onChange={e => setNewCourseCategory(e.target.value)}
                              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: CARD, color: TX, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                              <option value="">{t('university.choose_subject')}</option>
                              {newCourseCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('university.level_age')}</label>
                            <select value={newCourseAge} onChange={e => setNewCourseAge(e.target.value)}
                              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: CARD, color: TX, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                              {['12', '14', '16', '18', '21'].map(a => <option key={a} value={a}>{a}+</option>)}
                            </select>
                          </div>
                        </div>
                        {courseError && <p style={{ fontSize: 12, color: RED, margin: '12px 0 0' }}>{courseError}</p>}
                        <button onClick={createCourseShell} disabled={creatingCourse || !courseName.trim() || !newCourseCategory}
                          style={{ marginTop: 14, width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: courseName.trim() && newCourseCategory ? G : 'rgba(255,255,255,0.05)', color: courseName.trim() && newCourseCategory ? M : MU, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                          {creatingCourse ? 'Creating…' : 'Create & generate syllabus'}
                        </button>
                      </>
                    ) : newCourseId ? (
                      <SyllabusGenerator
                        classId={newCourseId}
                        targetAge={parseInt(newCourseAge) || 18}
                        subjectName={courseName}
                        existingPlan={null}
                        initialUseSearch={newCourseUseSearch}
                        onSyllabusSaved={() => { load() }}
                      />
                    ) : (
                      <p style={{ fontSize: 13, color: MU, textAlign: 'center', padding: 24 }}>
                        Creating your {org.courseSingular.toLowerCase()}…
                      </p>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Full creation — master catalog / community library / custom */}
            {showCreate && (
              <CreateCourseModal
                hubName={branding.display_name || undefined}
                loading={creatingCourse}
                error={courseError}
                onClose={() => { setShowCreate(false); setCourseError(null) }}
                onCreate={createCourse}
              />
            )}
          </div>
        )}

        {/* ─── STAFF TAB ─── */}
        {activeTab === 'staff' && (
          <div>
            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 28px', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: '0 0 8px' }}>Invite a {org.staffSingular}</h2>
              <p style={{ fontSize: 12.5, color: MU, margin: '0 0 16px', lineHeight: 1.6 }}>
                {org.staffLabel} receive a magic link to set up their account. They can generate {org.courseLabel.toLowerCase()} and see {org.learnerLabel.toLowerCase()} progress, scoped to your organisation only.
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {([{ id: 'teacher', label: org.staffSingular }, { id: 'admin', label: 'Administrator' }] as const).map(r => (
                  <button key={r.id} onClick={() => setInviteRole(r.id as 'teacher' | 'admin')}
                    style={{ padding: '8px 16px', borderRadius: 999, border: `2px solid ${inviteRole === r.id ? BLUE : BR}`, background: inviteRole === r.id ? 'rgba(14,165,233,0.08)' : 'transparent', color: inviteRole === r.id ? BLUE : MU, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {r.label}
                  </button>
                ))}
              </div>
              {inviteHint && (
                <div style={{ background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.3)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12.5, color: G, lineHeight: 1.4 }}>💡 {inviteHint}</span>
                  <button onClick={() => setInviteHint(null)} style={{ background: 'transparent', border: 'none', color: MU, cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Dr. Jane Smith"
                  style={{ flex: '1 1 160px', padding: '12px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.03)', color: TX, fontSize: 14, outline: 'none' }} />
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="jane@organisation.com"
                  style={{ flex: '2 1 220px', padding: '12px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.03)', color: TX, fontSize: 14, outline: 'none' }} />
                <button onClick={inviteStaff} disabled={inviteLoading || !inviteEmail.trim()}
                  style={{ padding: '12px 24px', borderRadius: 12, border: 'none', cursor: 'pointer', background: inviteEmail.trim() ? G : 'rgba(255,255,255,0.05)', color: inviteEmail.trim() ? M : MU, fontWeight: 800, fontSize: 13 }}>
                  {inviteLoading ? '...' : 'Invite'}
                </button>
              </div>
              {inviteMsg && <p style={{ fontSize: 12, color: inviteMsg.includes('sent') ? GR : RED, margin: '12px 0 0' }}>{inviteMsg}</p>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <button
                  onClick={() => openOffboarding()}
                  style={{ background: '#b91c1c', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' }}
                >
                  Offboard Teacher
                </button>
              </div>
              {staff.map(m => {
                const isExpanded = expandedStaffId === m.id
                const theirCourses = courses.filter(c => c.teacher_id === m.id)
                const theirStudentIds = new Set(theirCourses.flatMap(c => c.enrolled_student_ids || []))
                const theirMembers = members.filter(mem => theirStudentIds.has(mem.id))
                return (
                  <div key={m.id} style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 16, overflow: 'hidden' }}>
                    <div
                      onClick={() => setExpandedStaffId(isExpanded ? null : m.id)}
                      style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(212,168,67,0.1)', color: G, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Users size={16} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: TX, margin: 0 }}>{m.name || 'Unnamed'}</p>
                        <p style={{ fontSize: 12, color: MU, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</p>
                      </div>
                      {theirCourses.length > 0 && (
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: MU, whiteSpace: 'nowrap' }}>
                          {theirCourses.length} {theirCourses.length === 1 ? org.courseSingular.toLowerCase() : org.courseLabel.toLowerCase()} · {theirMembers.length} {theirMembers.length === 1 ? org.learnerSingular.toLowerCase() : org.learnerLabel.toLowerCase()}
                        </span>
                      )}
                      {m.facility_role === 'admin' && (
                        <span style={{ fontSize: 9.5, fontWeight: 800, color: G, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: 'rgba(212,168,67,0.12)' }}>Admin</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 800, color: m.is_active ? GR : MU2, textTransform: 'uppercase' }}>{m.is_active ? 'Active' : 'Invited'}</span>
                      <ChevronDown size={16} color={MU} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s', flexShrink: 0 }} />
                    </div>
                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${BR}`, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16, background: 'rgba(0,0,0,0.25)' }}>
                        {theirCourses.length === 0 && theirMembers.length === 0 ? (
                          <p style={{ fontSize: 12.5, color: MU2, margin: 0 }}>
                            No {org.courseLabel.toLowerCase()} or assigned {org.learnerLabel.toLowerCase()} yet. {org.courseLabel} they create or are given will appear here.
                          </p>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 16 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 800, color: MU, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <BookOpen size={12} color={BLUE} /> Their {org.courseLabel} ({theirCourses.length})
                              </div>
                              {theirCourses.length === 0 ? (
                                <p style={{ fontSize: 12, color: MU2, margin: 0 }}>None yet.</p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                  {theirCourses.map(c => (
                                    <button
                                      key={c.id}
                                      onClick={() => { setActiveTab('courses'); setEditingCourse(c) }}
                                      title="Open this course's detail"
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 11px', borderRadius: 10, border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.03)', color: TX, fontSize: 12.5, cursor: 'pointer' }}
                                    >
                                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.is_published ? '#10b981' : '#94a3b8', flexShrink: 0 }} />
                                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{c.name}</span>
                                      <span style={{ fontSize: 10.5, color: MU2, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {c.enrollment_mode === 'targeted' ? `${c.enrolled_student_ids?.length ?? 0} assigned` : 'whole org'}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 800, color: MU, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <GraduationCap size={12} color={G} /> Their {org.learnerLabel} ({theirMembers.length})
                              </div>
                              {theirMembers.length === 0 ? (
                                <p style={{ fontSize: 12, color: MU2, margin: 0 }}>None assigned yet.</p>
                              ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  {theirMembers.map(mem => (
                                    <button
                                      key={mem.id}
                                      onClick={() => openStaffStudentDetail(mem, theirCourses)}
                                      title="View this learner's progress"
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11.5, color: '#ffffff', cursor: 'pointer', opacity: mem.is_active ? 1 : 0.55 }}
                                    >
                                      <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 800, flexShrink: 0 }}>
                                        {(mem.name || mem.email).charAt(0).toUpperCase()}
                                      </span>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{mem.name || mem.email}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              {staff.length === 0 && (
                <div style={{ background: CARD, border: `1px dashed ${BR}`, borderRadius: 16, padding: '32px 20px', textAlign: 'center' }}>
                  <p style={{ color: MU, fontSize: 13 }}>No {org.staffLabel.toLowerCase()} yet. Invite your first above.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── LEARNERS TAB ─── */}
        {activeTab === 'learners' && (
          <div>
            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 28px', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: '0 0 8px' }}>Onboard a {org.learnerSingular}</h2>
              <p style={{ fontSize: 12.5, color: MU, margin: '0 0 16px', lineHeight: 1.6 }}>
                {org.learnerLabel} receive a magic link to start learning immediately, AI tutor, mastery quizzes, permanent evidence records, and verifiable credentials under your organisation's name.
              </p>
              {inviteHint && (
                <div style={{ background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.3)', borderRadius: 12, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12.5, color: G, lineHeight: 1.4 }}>💡 {inviteHint}</span>
                  <button onClick={() => setInviteHint(null)} style={{ background: 'transparent', border: 'none', color: MU, cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input type="email" value={learnerEmail} onChange={e => setLearnerEmail(e.target.value)}
                  placeholder={`${org.learnerSingular.toLowerCase()}@yourorg.com`}
                  style={{ flex: '2 1 220px', padding: '12px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.03)', color: TX, fontSize: 14, outline: 'none' }} />
                <select value={learnerClass} onChange={e => setLearnerClass(e.target.value)}
                  style={{ flex: '1 1 180px', padding: '12px 16px', borderRadius: 12, border: `2px solid ${BR}`, background: CARD, color: TX, fontSize: 14, outline: 'none' }}>
                  <option value="">Assign to {org.courseSingular.toLowerCase()} (optional)</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={inviteLearner} disabled={learnerLoading || !learnerEmail.trim()}
                  style={{ padding: '12px 24px', borderRadius: 12, border: 'none', cursor: 'pointer', background: learnerEmail.trim() ? G : 'rgba(255,255,255,0.05)', color: learnerEmail.trim() ? M : MU, fontWeight: 800, fontSize: 13 }}>
                  {learnerLoading ? '...' : `Invite ${org.learnerSingular.toLowerCase()}`}
                </button>
              </div>
              {learnerMsg && <p style={{ fontSize: 12, color: learnerMsg.includes('sent') ? GR : RED, margin: '12px 0 0' }}>{learnerMsg}</p>}
            </div>

            {/* ── Learner roster, deactivate blocks sign-in & releases the seat ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {members.map(m => (
                <div key={m.id} style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', opacity: m.is_active ? 1 : 0.55 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(14,165,233,0.1)', color: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <GraduationCap size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 700, color: TX, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || 'Unnamed'}</p>
                      <p style={{ fontSize: 12, color: MU, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                    {m.is_active && (
                      <button
                        onClick={() => resendInvite(m)}
                        disabled={resendingId === m.id || memberBusy === m.id}
                        style={{
                          padding: '7px 12px',
                          borderRadius: 8,
                          border: '1px solid rgba(14,165,233,0.3)',
                          background: 'rgba(14,165,233,0.08)',
                          color: BLUE,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          whiteSpace: 'nowrap'
                        }}
                        title="Resend magic link sign-in email"
                      >
                        <Mail size={13} />
                        {resendingId === m.id ? 'Sending…' : 'Resend Email'}
                      </button>
                    )}
                    <button onClick={() => toggleMember(m)} disabled={memberBusy === m.id}
                      style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${m.is_active ? 'rgba(248,113,113,0.3)' : 'rgba(74,222,128,0.3)'}`, background: m.is_active ? 'rgba(248,113,113,0.08)' : 'rgba(74,222,128,0.08)', color: m.is_active ? RED : GR, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {memberBusy === m.id ? '…' : m.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </div>
              ))}
              {members.length === 0 && (
                <div style={{ background: CARD, border: `1px dashed ${BR}`, borderRadius: 16, padding: '32px 20px', textAlign: 'center' }}>
                  <p style={{ color: MU, fontSize: 13 }}>No {org.learnerLabel.toLowerCase()} yet. Invite your first above.</p>
                </div>
              )}
              {memberMsg && <p style={{ fontSize: 12, color: memberMsg.includes('deactivated') || memberMsg.includes('reactivated') ? GR : RED, margin: 0 }}>{memberMsg}</p>}
            </div>
          </div>
        )}

        {/* ─── BILLING & SEATS TAB ─── */}
        {activeTab === 'billing' && (
          <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '28px 30px' }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: '0 0 4px' }}>{t('university.seats_billing')}</h2>
            <p style={{ fontSize: 12.5, color: MU, margin: '0 0 24px', lineHeight: 1.6 }}>
              {wlPlan
                ? `$${wlPlan.price_monthly}/seat per month · $${wlPlan.price_yearly}/seat per year (2 months free). Education and volume discounts available, contact us.`
                : 'Per-seat pricing, plans are being configured. Contact us to activate seats today.'}
            </p>
            

            {seatInfo && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  { label: 'Seats purchased', value: seatInfo.total },
                  { label: 'Seats in use', value: seatInfo.used },
                ].map(s => (
                  <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 12, padding: '10px 16px' }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: TX }}>{s.value}</span>
                    <span style={{ fontSize: 10.5, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginLeft: 8 }}>{s.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Seat stepper */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.03)', border: `2px solid ${BR}`, borderRadius: 14, padding: '14px 18px', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: TX, margin: 0 }}>{org.learnerLabel} seats</p>
                <p style={{ fontSize: 11, color: MU2, margin: '2px 0 0' }}>{t('university.each_seat_licenses_one_learner_adjust')}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setSeatCount(s => Math.max(1, s - 1))} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${BR}`, background: 'transparent', color: TX, fontSize: 18, fontWeight: 800, cursor: 'pointer' }}>−</button>
                <span style={{ fontSize: 22, fontWeight: 800, color: TX, minWidth: 40, textAlign: 'center' }}>{seatCount}</span>
                <button onClick={() => setSeatCount(s => Math.min(500, s + 1))} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${BR}`, background: 'transparent', color: TX, fontSize: 18, fontWeight: 800, cursor: 'pointer' }}>+</button>
              </div>
            </div>

            {/* Interval pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {([
                { id: 'month', label: 'Monthly' },
                { id: 'six_months', label: '6 months' },
                { id: 'year', label: '12 months' },
              ] as const).map(iv => (
                <button key={iv.id} onClick={() => setBillingInterval(iv.id)}
                  style={{ flex: 1, padding: '11px', borderRadius: 12, border: `2px solid ${billingInterval === iv.id ? G : BR}`, background: billingInterval === iv.id ? 'rgba(212,168,67,0.08)' : 'transparent', color: billingInterval === iv.id ? G : MU, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                  {iv.label}
                </button>
              ))}
            </div>

            {/* Total + CTA */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, paddingTop: 16, borderTop: `1px solid ${BR}` }}>
              <div>
                <p style={{ fontSize: 11, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Total</p>
                <p style={{ fontSize: 26, fontWeight: 800, color: TX, margin: '2px 0 0' }}>
                  {checkoutTotal !== null ? `$${checkoutTotal.toLocaleString()}` : '—'}
                  <span style={{ fontSize: 12, color: MU, fontWeight: 600 }}>{billingInterval === 'month' ? ' /month' : billingInterval === 'six_months' ? ' /6mo' : ' /year'}</span>
                </p>
              </div>
              <button onClick={buySeats} disabled={checkoutLoading || !wlPlan || !facilityId}
                style={{ padding: '14px 32px', borderRadius: 14, border: 'none', cursor: 'pointer', background: wlPlan ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)', color: wlPlan ? M : MU, fontWeight: 800, fontSize: 14 }}>
                {checkoutLoading ? 'Opening checkout…' : 'Subscribe & activate seats'}
              </button>
            </div>
            {checkoutError && <p style={{ fontSize: 12.5, color: RED, margin: '12px 0 0' }}>{checkoutError}</p>}
            <p style={{ fontSize: 11, color: MU2, margin: '16px 0 0', lineHeight: 1.5 }}>
              Billed through Stripe. Manage payment methods, invoices, and seat quantity in the billing portal.
            </p>
            <button onClick={openPortal} disabled={portalLoading}
              style={{ marginTop: 12, padding: '12px 24px', borderRadius: 12, border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {portalLoading ? 'Opening…' : 'Manage billing & seats'}
            </button>
          </div>
        )}

        {/* ─── ANALYTICS TAB ─── */}
        {activeTab === 'analytics' && (
          <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '28px 30px' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: '0 0 8px' }}>Analytics</h2>
            <p style={{ fontSize: 12.5, color: MU, margin: '0 0 24px', lineHeight: 1.6 }}>
              Engagement, mastery distribution, and credential issuance across your {org.learnerLabel.toLowerCase()}.
              Every metric backed by server-verified evidence.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 14 }}>
              {[
                { label: `Total ${org.learnerLabel.toLowerCase()}`, value: totalEnrolled, sub: `Across all ${org.courseLabel.toLowerCase()}` },
                { label: org.courseLabel, value: courses.length, sub: `${courses.filter(c => c.delivery_mode === 'theory').length} theory, ${courses.filter(c => c.delivery_mode === 'practical').length} practical` },
                { label: `Active ${org.staffLabel.toLowerCase()}`, value: staff.filter(m => m.is_active).length, sub: 'Generating and teaching' },
                { label: 'Credentials issued', value: '—', sub: 'Auto-issued at 90% mastery' },
              ].map((s, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 16, padding: '18px 20px' }}>
                  <p style={{ fontSize: 26, fontWeight: 800, color: TX, margin: 0 }}>{s.value}</p>
                  <p style={{ fontSize: 11, color: MU, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '6px 0 2px' }}>{s.label}</p>
                  <p style={{ fontSize: 11, color: MU2, margin: 0 }}>{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Manager learner detail (drill-down) ── */}
      {/* ── Teacher offboarding overlay (Kit 15) ── */}
      {offboardOpen && (
        <div
          onClick={closeOffboarding}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, boxShadow: '0 24px 60px rgba(15,23,42,0.28)', width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', padding: 22 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Offboard a teacher</h3>
              <button onClick={closeOffboarding} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MU, flexShrink: 0 }}><X size={20} /></button>
            </div>

            {!offboardResult && (
              <>
                <p style={{ fontSize: 13, margin: '14px 0 6px', fontWeight: 700 }}>Who is leaving?</p>
                <select
                  value={offboardDepartingId}
                  onChange={e => { setOffboardDepartingId(e.target.value); setOffboardTargetId(''); loadOffboardPreview(e.target.value) }}
                  disabled={offboardBusy}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, background: '#fff' }}
                >
                  <option value="">Select the departing teacher...</option>
                  {staff.filter(s => s.id !== user?.id).map(s => (
                    <option key={s.id} value={s.id}>{s.name || s.email}</option>
                  ))}
                </select>

                <p style={{ fontSize: 13, margin: '14px 0 6px', fontWeight: 700 }}>Who takes over their courses and learners?</p>
                <select
                  value={offboardTargetId}
                  onChange={e => setOffboardTargetId(e.target.value)}
                  disabled={offboardBusy || !offboardDepartingId}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, background: '#fff' }}
                >
                  <option value="">Select who takes over...</option>
                  {offboardDepartingId && staff.filter(s => s.id !== offboardDepartingId).map(s => (
                    <option key={s.id} value={s.id}>{s.name || s.email}{s.id === user?.id ? ' (you)' : ''}</option>
                  ))}
                </select>

                <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#f8fafc', fontSize: 13, lineHeight: 1.6 }}>
                  {offboardDepartingId ? (offboardPreview ? (
                    <>
                      <div><b>{offboardPreview.courses}</b> course{offboardPreview.courses === 1 ? '' : 's'} will transfer</div>
                      <div><b>{offboardPreview.students}</b> assigned learner{offboardPreview.students === 1 ? '' : 's'} will reassign</div>
                      <div><b>{offboardPreview.coTeachLinks}</b> co-teaching link{offboardPreview.coTeachLinks === 1 ? '' : 's'} will be removed</div>
                    </>
                  ) : (
                    <div>Counting what needs to move...</div>
                  )) : (
                    <div>Select a departing teacher to see what will move.</div>
                  )}
                  <div style={{ marginTop: 6, color: MU, fontSize: 12 }}>
                    Admin-initiated transfers apply immediately — the recipient is not asked to consent.
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={closeOffboarding} disabled={offboardBusy} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                  <button
                    onClick={runOffboarding}
                    disabled={offboardBusy || !offboardDepartingId || !offboardTargetId}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: 'none', background: offboardDepartingId && offboardTargetId ? '#b91c1c' : '#e2e8f0', color: offboardDepartingId && offboardTargetId ? '#fff' : '#94a3b8', cursor: offboardDepartingId && offboardTargetId ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700 }}
                  >
                    {offboardBusy ? 'Working...' : 'Reassign everything'}
                  </button>
                </div>
              </>
            )}

            {offboardResult && (
              <div style={{ marginTop: 16 }}>
                <div style={{ padding: 12, borderRadius: 10, background: offboardResult.failed === 0 ? '#f0fdf4' : '#fef2f2', fontSize: 13 }}>
                  <b>{offboardResult.done}</b> item{offboardResult.done === 1 ? '' : 's'} moved.
                  {offboardResult.failed > 0 && <> <b style={{ color: '#b91c1c' }}>{offboardResult.failed}</b> failed — see below, reload, then re-run for anything left.</>}
                </div>
                {offboardResult.errors.length > 0 && (
                  <div style={{ marginTop: 10, maxHeight: 160, overflowY: 'auto', fontSize: 12, color: '#b91c1c', lineHeight: 1.5 }}>
                    {offboardResult.errors.map((er, i) => <div key={i}>• {er}</div>)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button onClick={closeOffboarding} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Close</button>
                  <button onClick={() => window.location.reload()} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Reload page</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {staffStudentDetail && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(6,6,12,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setStaffStudentDetail(null)}
        >
          <motion.div
            initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
            onClick={e => e.stopPropagation()}
            style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: 28, maxWidth: 460, width: '100%', maxHeight: '80vh', overflowY: 'auto', color: TX, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', boxSizing: 'border-box' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: 0 }}>{staffStudentDetail.member.name || 'Unnamed learner'}</h3>
                <p style={{ fontSize: 12, color: MU, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{staffStudentDetail.member.email}</p>
              </div>
              <button onClick={() => setStaffStudentDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MU, flexShrink: 0 }}><X size={20} /></button>
            </div>
            {staffStudentLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: MU }}>
                <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading progress…
              </div>
            ) : staffStudentDetail.rows.length === 0 ? (
              <p style={{ fontSize: 12.5, color: MU, margin: 0 }}>No course activity for this {org.learnerSingular.toLowerCase()} yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {staffStudentDetail.rows.map(r => (
                  <div key={r.courseId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}` }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: TX, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.courseName}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: r.mastery == null ? MU2 : r.mastery >= 90 ? GR : G, whiteSpace: 'nowrap' }}>
                      {r.mastery == null ? 'Not started' : `${Math.round(r.mastery)}% mastery`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* ── Transfer confirmation overlay ── */}
      {transferPending && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 400,
            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            style={{
              background: CARD, border: `1px solid ${BR}`,
              borderRadius: 20, padding: 32, maxWidth: 480, width: '100%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            }}
          >
            {transferPending.sent ? (
              <>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(74,222,128,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                  <CheckCircle size={26} color={GR} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: '0 0 8px', fontFamily: serif }}>
                  Transfer Request Sent
                </h3>
                <p style={{ fontSize: 14, color: MU, lineHeight: 1.65, margin: '0 0 16px' }}>
                  Transfer request sent to <strong style={{ color: TX }}>{transferPending.user_name}</strong>: they have 7 days to accept.
                </p>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 12, padding: '14px 16px', marginBottom: 24 }}>
                  <p style={{ fontSize: 12.5, color: MU2, lineHeight: 1.6, margin: 0 }}>
                    A secure transfer authorization link was emailed to <strong style={{ color: TX }}>{transferPending.email}</strong>. If they accept, their active account moves over immediately. Any history they earned there remains permanently on record with that organisation.
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    onClick={reinviteWithSecondEmail}
                    style={{
                      width: '100%', padding: '13px 0', borderRadius: 12, border: `1px solid rgba(212,168,67,0.3)`,
                      background: 'rgba(212,168,67,0.08)', color: G, fontWeight: 800, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    Invite with a different email instead
                  </button>
                  <button
                    onClick={() => setTransferPending(null)}
                    style={{
                      width: '100%', padding: '13px 0', borderRadius: 12, border: `1px solid ${BR}`,
                      background: 'transparent', color: MU, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(212,168,67,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                  <span style={{ fontSize: 26 }}>📨</span>
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: TX, margin: '0 0 8px', fontFamily: serif }}>
                  {transferPending.target_role === 'teacher' || transferPending.target_role === 'admin'
                    ? `${org.staffSingular} is in another organisation`
                    : `${org.learnerSingular} is in another organisation`}
                </h3>
                <p style={{ fontSize: 14, color: MU, lineHeight: 1.65, margin: '0 0 20px' }}>
                  <strong style={{ color: TX }}>{transferPending.user_name}</strong> ({transferPending.email}) is already registered with another organisation. {transferPending.target_role === 'teacher' || transferPending.target_role === 'admin' ? org.staffLabel : org.learnerLabel} can belong to one organisation at a time.
                </p>
                <div style={{ background: 'rgba(212,168,67,0.06)', border: `1px solid rgba(212,168,67,0.2)`, borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                  <p style={{ fontSize: 13, color: MU, lineHeight: 1.6, margin: 0 }}>
                    <strong style={{ color: G }}>Send a transfer request?</strong><br />
                    We'll email <strong style={{ color: TX }}>{transferPending.user_name}</strong> a secure link. <strong style={{ color: TX }}>They decide</strong> whether to move their account to your organisation, their current organisation is not involved in the decision and cannot release or block them. If they accept, their account moves over immediately. If they decline, you can invite them with a different email address instead.
                  </p>
                  <p style={{ fontSize: 12, color: MU2, margin: '10px 0 0', lineHeight: 1.5 }}>
                    Any history they earned there remains permanently on record with that organisation.
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={handleSendTransfer}
                      disabled={transferLoading}
                      style={{
                        flex: 1, padding: '13px 0', borderRadius: 12, border: 'none',
                        background: BLUE, color: '#fff', fontWeight: 800, fontSize: 14,
                        cursor: transferLoading ? 'not-allowed' : 'pointer',
                        opacity: transferLoading ? 0.7 : 1,
                      }}
                    >
                      {transferLoading ? '…' : '📧 Send Transfer Request'}
                    </button>
                    <button
                      onClick={() => setTransferPending(null)}
                      disabled={transferLoading}
                      style={{
                        padding: '13px 20px', borderRadius: 12, border: `1px solid ${BR}`,
                        background: 'transparent', color: MU, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                  <button
                    onClick={reinviteWithSecondEmail}
                    disabled={transferLoading}
                    style={{
                      width: '100%', padding: '10px 0', borderRadius: 10, border: `1px dashed ${BR}`,
                      background: 'transparent', color: MU, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Invite with a different email instead
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
